// src/data/resources.js

export const resourceCategories = [
  "Housing",
  "Primary SUD Providers",
  "Primary Mental Health Providers",
  "Therapists",
  "Ride Services",
  "Crisis Intervention Services",
  "Online Resources and Support Groups",
];

// Define subcategories for each main category
export const subcategories = {
  Housing: ["Shelters", "General Housing", "Recovery Homes", "Homelessness"],
  "Primary SUD Providers": [
    "Detox",
    "Treatment Centers",
    "Respite",
    "RCO",
    "PSC",
  ],
  // Add more categories here if needed, e.g., "Primary Mental Health Providers": [...]
};

// Explanatory content for service types. These appear above provider cards and
// are intentionally kept separate from the resource records and counts.
export const subcategoryGuidance = {
  "Primary SUD Providers": {
    Detox: {
      title: "About withdrawal management (detox)",
      description:
        "Detox provides medical support while a person safely stops using alcohol or other drugs. Capabilities vary by facility, especially for alcohol, benzodiazepines, and higher methadone doses, so confirm that a program can meet the person's specific medical needs before arrival.",
    },
    "Treatment Centers": {
      title: "About treatment centers",
      description:
        "Treatment centers provide structured substance use care at different levels, including residential, outpatient, and intensive outpatient programs. Contact each provider to confirm its current programs, eligibility requirements, insurance coverage, and availability.",
    },
    Respite: {
      title: "About respite care",
      description:
        "Respite offers short-term, supportive care in a safe, substance-free setting. It can provide stabilization, rest, and a bridge between a crisis service and ongoing treatment.",
    },
    RCO: {
      title: "About recovery community organizations",
      description:
        "Recovery Community Organizations (RCOs) are peer-led, peer-run agencies providing no-cost, low-barrier recovery support. Services may include peer recovery coaching, mutual aid groups, family support, harm reduction, system navigation, and advocacy.",
    },
    PSC: {
      title: "About peer support centers",
      description:
        "New Hampshire Peer Support Centers are member-run programs offering non-clinical peer support, groups, recovery activities, and warmline services. Contact an individual center to confirm its current hours and programs.",
      link: "https://www.dhhs.nh.gov/programs-services/mental-health/peer-support-agencies",
      linkLabel: "View the NH DHHS peer support directory",
    },
  },
};

export const counties = [
  "All",
  "Belknap",
  "Carroll",
  "Cheshire",
  "Coös",
  "Grafton",
  "Hillsborough",
  "Merrimack",
  "Rockingham",
  "Strafford",
  "Sullivan",
];

// Helper functions
export const hasSubcategories = (category) => {
  return subcategories.hasOwnProperty(category);
};

export const getSubcategories = (category) => {
  return subcategories[category] || [];
};

export const getResourcesByCategory = (category) => {
  return resources.filter((resource) => {
    // Handle both string and array categories
    if (Array.isArray(resource.category)) {
      return resource.category.includes(category);
    }
    return resource.category === category;
  });
};

export const getResourcesByCounty = (county) => {
  if (county === "All") return resources;
  return resources.filter(
    (resource) =>
      resource.statewide === true || resource.counties.includes(county),
  );
};

export const getResourcesByCategoryAndCounty = (category, county) => {
  let filtered = resources.filter((resource) => {
    // Handle both string and array categories
    if (Array.isArray(resource.category)) {
      return resource.category.includes(category);
    }
    return resource.category === category;
  });

  if (county !== "All") {
    filtered = filtered.filter(
      (resource) =>
        resource.statewide === true || resource.counties.includes(county),
    );
  }
  return filtered;
};

// New function for subcategory filtering
export const getResourcesBySubcategory = (
  category,
  subcategory,
  county = "All",
) => {
  let filtered = resources.filter((resource) => {
    // Check if resource belongs to the category (handle both string and array)
    const belongsToCategory = Array.isArray(resource.category)
      ? resource.category.includes(category)
      : resource.category === category;

    // Check if resource belongs to the subcategory (handle both string and array)
    const belongsToSubcategory = Array.isArray(resource.subcategory)
      ? resource.subcategory.includes(subcategory)
      : resource.subcategory === subcategory;

    return belongsToCategory && belongsToSubcategory;
  });

  if (county !== "All") {
    filtered = filtered.filter(
      (resource) =>
        resource.statewide === true || resource.counties.includes(county),
    );
  }

  return filtered;
};

// Function to get resources without subcategories (for categories like Housing with mixed content)
export const getResourcesWithoutSubcategory = (category, county = "All") => {
  let filtered = resources.filter((resource) => {
    // Check if resource belongs to the category (handle both string and array)
    const belongsToCategory = Array.isArray(resource.category)
      ? resource.category.includes(category)
      : resource.category === category;

    return belongsToCategory && !resource.subcategory;
  });

  if (county !== "All") {
    filtered = filtered.filter(
      (resource) =>
        resource.statewide === true || resource.counties.includes(county),
    );
  }

  return filtered;
};

export const resources = [
  // Housing Resources (without subcategory)
  {
    id: 1,
    name: "NHCORR",
    category: "Housing",
    subcategory: "Recovery Homes",
    counties: [
      "All",
      "Belknap",
      "Carroll",
      "Cheshire",
      "Coös",
      "Grafton",
      "Hillsborough",
      "Merrimack",
      "Rockingham",
      "Strafford",
      "Sullivan",
    ],
    address: "Concord, NH",
    phone: "(603) 715-1514",
    website: "https://www.nhcorr.org",
    email: "admin@nhcorr.org",
    services: [
      "Recovery housing certification (NARR standards)",
      "Rental / housing assistance programs",
      "Education, training & advocacy",
      "Ombudsman & mediation for certified homes",
    ],
    description:
      "The New Hampshire Coalition of Recovery Residences (NHCORR) is a nonprofit organization established in 2017 and is the sole New Hampshire affiliate of the National Alliance for Recovery Residences (NARR). NHCORR certifies recovery homes to nationally recognized standards of safety, recovery support, and ethical operation. It also provides rental assistance, education and training, advocacy, and ombudsman services to support both residents and operators across the state.",
    notes:
      "Headquartered in Concord, NH, NHCORR has certified over 100 homes statewide and has administered over $1.2 million in housing assistance. For grievances or complaints, residents can reach out to the Ombudsman via admin@nhcorr.org.",
  },
  {
    id: 149,
    name: "New Hampshire Partnership for Recovery Residences (NHPRR)",
    category: "Housing",
    subcategory: "Recovery Homes",
    counties: [
      "All",
      "Belknap",
      "Carroll",
      "Cheshire",
      "Coös",
      "Grafton",
      "Hillsborough",
      "Merrimack",
      "Rockingham",
      "Strafford",
      "Sullivan",
    ],
    address: "",
    phone: "",
    website: "https://nhprr.com",
    email: "",
    services: [
      "State certification of recovery residences using SAMHSA standards",
      "Required for eligibility to receive state funding from state-funded providers",
      "Facilitates registration in NH Voluntary Recovery House Registry",
    ],
    description:
      "NHPRR is the agency designated by the State of New Hampshire to certify recovery residences to SAMHSA standards. Certification through NHPRR is required for homes to qualify for state funds or receive referrals from state-funded systems (e.g., treatment providers, drug courts, Doorway).",
    notes:
      "NHCORR remains the state affiliate of NARR and certifies homes to national NARR standards. NHPRR certification enables inclusion in the state’s registry and access to public funding.",
  },

  {
    id: 3,
    name: "2-1-1 New Hampshire",
    category: [
      "Housing",
      "Primary SUD Providers",
      "Primary Mental Health Providers",
      "Ride Services",
      "Crisis Intervention Services",
    ],
    statewide: true,
    subcategory: "General Housing",
    counties: [
      "All",
      "Belknap",
      "Carroll",
      "Cheshire",
      "Coös",
      "Grafton",
      "Hillsborough",
      "Merrimack",
      "Rockingham",
      "Strafford",
      "Sullivan",
    ],
    address: "P.O. Box 211, Manchester, NH 03105",
    phone: "2-1-1 (NH only); toll-free 1-866-444-4211 (out-of-state)",
    website: "https://211nh.org",
    email: "211nh@211nh.org",
    services: [
      "24/7 confidential connection to housing, utility, food, mental health, substance use, legal, and other health & human services",
      "Certified Information & Referral Specialists",
      "Multilingual and TDD (TTY) access",
      "Online searchable resource database",
    ],
    description:
      "2-1-1 New Hampshire is a free, 24-hour, statewide information and referral service that connects residents with critical health and human services—ranging from housing, utilities, food, and mental health support to substance use resources and legal aid—through trained specialists and an online directory.",
    notes:
      "Operated as an initiative of the United Ways across NH, 2-1-1 provides services 365 days a year and ensures confidentiality. It offers access via phone (2-1-1 in-state, 1-866-444-4211 from out-of-state), email inquiries (211nh@211nh.org), and an online search portal. Multilingual and TDD services are also available.",
  },
  {
    id: 4,
    name: "CAP NH",
    category: "Housing",
    subcategory: "General Housing",
    counties: [
      "All",
      "Belknap",
      "Carroll",
      "Cheshire",
      "Coös",
      "Grafton",
      "Hillsborough",
      "Merrimack",
      "Rockingham",
      "Strafford",
      "Sullivan",
    ],
    address:
      "30 Exchange Street, Berlin, NH 03570 (Main Office Statewide Coordination)",
    phone: "(603) 752-7001 (Tri-County main office)",
    website: "https://www.capnh.org",
    email:
      "info@caphr.org (for Hillsborough & Rockingham), contactus@capbm.org (Belknap-Merrimack), cap@straffordcap.org (Strafford)",
    services: [
      "Housing assistance & homelessness prevention",
      "Weatherization®, electric & fuel assistance",
      "Financial literacy & budgeting education",
      "Job training & workforce development",
      "Childcare and early childhood education (Head Start/Early Head Start)",
      "Senior services including Meals on Wheels",
      "Food assistance, nutrition programs, WIC, mobile pantries",
      "Transportation services",
      "Diaper distribution & family support",
      "Legal, advocacy, and outreach services",
    ],
    description:
      "CAP NH is the statewide network of five Community Action Agencies in New Hampshire dedicated to reducing poverty and building self-sufficiency. It coordinates a wide array of services—from housing and energy assistance to education, workforce development, early childhood programs, senior support, and food security—through its member agencies across every region of the state.",
    notes:
      "Member agencies include: Tri-County C.A.P. (Coos, Carroll, Grafton Counties), Community Action Partnership of Strafford County, Community Action Program Belknap-Merrimack (CAPBM), Community Action Partnership Hillsborough & Rockingham (CAPHR), and Southwestern Community Services (Cheshire & Sullivan). Each agency provides tailored programs and local offices. General contact emails are info@caphr.org (CAPHR), contactus@capbm.org (CAPBM), and cap@straffordcap.org (Strafford CAP). The network’s main site is capnh.org.",
  },
  {
    id: 5,
    name: "Veterans Inc.",
    category: "Housing",
    statewide: true,
    subcategory: "General Housing",
    counties: ["All", "Merrimack"],
    address: "10 Ferry St., Suite 404, Concord, NH 03301 (regional office)",
    phone: "1-800-482-2565 (24/7 toll-free)",
    website: "https://www.veteransinc.org",
    email: "info@veteransinc.org",
    services: [
      "Emergency, transitional, and permanent supportive housing",
      "Intensive case management and counseling",
      "Supportive Services for Veteran Families (SSVF): housing stabilization, rent/utilities assistance, budgeting, childcare, transportation, legal and healthcare referrals",
      "Employment and training programs (including HVRP & CODE)",
      "Food services and nutritional support",
      "Health & wellness services, including mental health and substance use support",
      "Suicide prevention, women's & children’s programs, post-9/11 veteran support",
      "Discount prescription card program",
    ],
    description:
      "Veterans Inc. is the largest provider of support services to veterans and their families in New England, using a holistic, clinical case-management model to address 'the total veteran.' Through its Concord office and regional partners, it offers housing programs, employment and training, wellness and mental health support, and financial and legal assistance across New England.",
    notes:
      "Founded in 1990 and headquartered in Worcester, MA, Veterans Inc. operates across all six New England states, plus Montana and North Dakota. The Concord, NH office delivers SSVF and employment & training services specifically in the state. Contact Cynthia M. Langdon, SSVF Shallow Subsidy Coordinator, at 603-247-5283 or cynthialangdon@veteransinc.org for direct assistance in New Hampshire.",
  },

  {
    id: 6,
    name: "Homelessness Prevention & Housing (CAPHR)",
    category: "Housing",
    subcategory: "Homelessness",
    counties: ["All", "Hillsborough", "Rockingham"],
    address:
      "40 Pine St, Manchester, NH 03103 (Admin Office & Resource Center)",
    phone:
      "Hillsborough: (603) 668-8010; Rockingham: (603) 431-2911; Toll-free: (800) 322-1073",
    website: "https://www.caphr.org/services/homelessness-prevention-housing",
    email: "info@caphr.org",
    services: [
      "Eviction prevention (short-term assistance)",
      "Housing search and placement",
      "Emergency shelter referrals via HUD Coordinated Entry",
      "Supportive housing: Robinson House (24 SRO units for men who completed substance use treatment); rent up to 30% of income",
    ],
    description:
      "CAPHR’s Homelessness Prevention & Housing program serves Hillsborough and Rockingham Counties with eviction prevention, rapid re-housing support, and coordinated-entry shelter referrals. Robinson House provides non-treatment supportive housing (SRO) for eligible men who have completed substance use treatment.",
    notes:
      "Eligibility: all ages in Hillsborough & Rockingham who meet HUD homelessness or at-risk criteria. Robinson House: 49 Manchester St, Manchester, NH 03101; phone (603) 627-9188; apply via Manchester Housing Authority; currently on waitlist per CAPHR site.",
  },

  {
    id: 7,
    name: "Greater Nashua Mental Health Center",
    category: "Housing",
    subcategory: "General Housing",
    counties: ["All", "Hillsborough"],
    address:
      "440 Amherst Street, Nashua, NH 03063 (plus additional clinic locations)",
    phone: "(603) 889-6147",
    website: "https://gnmhc.org/",
    email: "",
    services: [
      "Homeless outreach & roommate-matching housing support",
      "Subsidized housing assistance stipends",
      "24/7 Rapid Response Behavioral Health crisis support (call/text 833-710-6477 or chat via NH988.com)",
    ],
    description:
      "GNMHC is the community mental health center serving the Greater Nashua region. It provides outreach services to individuals with mental health needs who are experiencing housing instability, including supported housing match-making and short-term subsidies.",
    notes:
      "Crisis hotline accessible to all NH residents. Additional services include behavioral health, deaf services, child/adolescent support, and adult services across multiple Nashua locations.",
  },
  {
    id: 8,
    name: "Seacoast Family Promise",
    category: "Housing",
    subcategory: "Homelessness",
    counties: ["All", "Rockingham", "Strafford"],
    address: "27 Hampton Road, Exeter, NH 03833",
    phone: "(603) 658-8448",
    website: "https://seacoastfamilypromise.org",
    email: "",
    services: [
      "Emergency shelter and transitional housing for families with children",
      "Trauma-informed case management and housing search assistance",
      "Financial education, parenting support, employment guidance",
    ],
    description:
      "Seacoast Family Promise provides temporary shelter and structured support to families with children experiencing homelessness—helping them regain self-sufficiency through education, employment assistance, and a caring community.",
    notes:
      "Operates through local congregational networks offering shelter, meals, and wrap-around services on the Seacoast.",
  },
  {
    id: 9,
    name: "Families In Transition",
    category: "Housing",
    subcategory: [
      "General Housing",
      "Shelters",
    ],
    counties: ["All", "Hillsborough", "Merrimack"],
    address:
      "122 Market Street, Manchester, NH 03101 (also 293 Wilson St, Manchester, NH)",
    phone: "(603) 641-9441",
    website: "https://www.fitnh.org/services/",
    email: "info@fitnh.org",
    services: [
      "Affordable and supportive housing for low-income families and individuals",
      "Emergency shelter for adults and families with children",
      "Food pantry and integrated case management",
      "Transitional housing and substance use treatment",
    ],
    description:
      "Families In Transition (FIT) supports low-income individuals and families in Manchester through a full continuum—from emergency shelter to permanent housing—along with food, wellness support, and case management.",
    notes: "For emergency shelter, call 211. For housing, call (603) 641-9441 ext. 257; for substance use treatment or recovery housing, use ext. 401.",
  },
  {
    id: 10,
    name: "Concord Coalition to End Homelessness",
    category: "Housing",
    subcategory: "Homelessness",
    counties: ["All", "Merrimack"],
    address: "238 N. Main Street, Concord, NH 03301",
    phone: "(603) 290-3375",
    website: "https://concordhomeless.org/get-help/",
    email: "office@concordhomeless.org",
    services: [
      "Resource Center with basic needs support (mailing, phones, showers, laundry, charging)",
      "Emergency Winter Shelter (December–March)",
      "Permanent Supportive Housing programs",
      "Case management and housing navigation",
    ],
    description:
      "CCEH operates a Resource Center offering vital daily services and referral support, runs an Emergency Winter Shelter seasonally, and facilitates permanent supportive housing—all aimed at ending homelessness in Concord.",
    notes:
      "Exec Director Karen Jantzen can be reached at karen@concordhomeless.org.",
  },
  {
    id: 11,
    name: "The Friends Program",
    category: "Housing",
    statewide: true,
    subcategory: "Homelessness",
    counties: ["All", "Merrimack"],
    address: "130 Pembroke Road, Suite 200, Concord, NH 03301",
    phone: "(603) 228-1193 (NH wide: 800-536-1193)",
    website: "https://www.friendsprogram.org",
    email: "",
    services: [
      "Emergency housing shelter for families with intensive case management",
      "Programs to build housing stability, healthy aging, educational support, food security",
      "Volunteer-driven community support across NH",
    ],
    description:
      "The Friends Program works statewide to connect at-risk youth, families, and seniors with housing, educational, and food security solutions—powered by volunteers and case managers providing wrap-around support.",
    notes:
      "Emergency Housing program detail and general operations outlined; contact through Concord office.",
  },
  // Housing – Shelters
  {
    id: 2,
    name: "Monadnock Area Transitional Shelter",
    category: "Housing",
    subcategory: "Shelters",
    counties: ["All", "Cheshire"],
    address:
      "Peterborough, NH (location confidential; mailing: PO Box 3053, Peterborough, NH 03458)",
    phone: "(603) 924-5033",
    website: "https://www.matsnh.org",
    email: "info@matsnh.org",
    services: [
      "Transitional Housing",
      "Case Management",
      "Supportive Services",
    ],
    description:
      "Transitional shelter serving the Monadnock region; housing plus case management toward permanent housing.",
    notes:
      "Apply via phone or town welfare; serves Cheshire & western Hillsborough; address kept confidential.",
  },
  {
    id: 62,
    name: "Belknap House",
    category: "Housing",
    subcategory: "Shelters",
    counties: ["All", "Belknap"],
    address: "200 Court St, Laconia, NH 03246",
    phone: "(603) 527-8097",
    website: "https://www.belknaphouse.org",
    email: "execdir@belknaphouse.org",
    services: ["Emergency Shelter (Families)", "Case Management"],
    description: "Year-round emergency shelter for families in Belknap County.",
    notes: "Referral typically via local welfare; contact for availability.",
  },
  {
    id: 63,
    name: "Isaiah's Cafe (Seasonal)",
    category: "Housing",
    subcategory: "Shelters",
    counties: ["All", "Belknap"],
    address: "100 New Salem St, Laconia, NH 03246",
    phone: "(603) 524-6161",
    website: "https://isaiah61cafe.org/",
    email: "",
    services: ["Seasonal Winter Shelter", "Meals"],
    description:
      "Seasonal overnight warming shelter with meal services in Laconia.",
    notes:
      "Typically operates Dec–Mar; confirm annual dates/hours before arrival.",
  },
  {
    id: 64,
    name: "Tyler Blain House (Tri-County CAP)",
    category: "Housing",
    subcategory: "Shelters",
    counties: ["All", "Coös"],
    address: "56 Prospect St, Lancaster, NH 03584",
    phone: "(603) 788-2344",
    website: "https://tccap.org/housing-stability/",
    email: "",
    services: ["Emergency Shelter", "Support Services"],
    description:
      "Emergency shelter serving Coös County, operated by Tri-County CAP.",
    notes: "Call for intake and availability.",
  },
  {
    id: 65,
    name: "Bancroft House",
    category: "Housing",
    subcategory: "Shelters",
    counties: ["All", "Grafton"],
    address: "Franconia, NH (address confidential)",
    phone: "(603) 823-8842",
    website: "https://thebancrofthouse.com",
    email: "",
    services: ["Emergency Shelter (Women & Families)"],
    description:
      "Shelter for women and families in the Franconia/Littleton area.",
    notes: "Address confidential; call for intake.",
  },
  {
    id: 66,
    name: "Bridge House",
    category: "Housing",
    subcategory: "Shelters",
    counties: ["All", "Grafton"],
    address: "260 Highland St, Plymouth, NH 03264",
    phone: "(603) 536-7631",
    website: "https://bhshelter.org",
    email: "info@tbhshelter.org",
    services: [
      "Emergency Shelter",
      "Transitional Housing",
      "Veterans Services",
      "Support Services",
    ],
    description:
      "Emergency shelter and stabilization support; specialized services for veterans.",
    notes:
      "Thrift/storefront number sometimes listed as (603) 238-9016; call main line for shelter intake.",
  },
  {
    id: 68,
    name: "Nashua Soup Kitchen & Shelter",
    category: "Housing",
    subcategory: "Shelters",
    counties: ["All", "Hillsborough"],
    address: "2 Quincy St, Nashua, NH 03060",
    phone: "(603) 889-7770",
    website: "https://nsks.org",
    email: "",
    services: ["Emergency Shelter", "Meals", "Food Pantry", "Support Services"],
    description: "Emergency shelter and food programs serving Greater Nashua.",
    notes:
      "Shelter access often coordinated via Greater Nashua Coordinated Entry: 1-844-800-9911.",
  },
  {
    id: 69,
    name: "Cross Roads House",
    category: "Housing",
    subcategory: "Shelters",
    counties: ["All", "Rockingham"],
    address: "600 Lafayette Rd, Portsmouth, NH 03801",
    phone: "(603) 436-2218",
    website: "https://www.crossroadshouse.org",
    email: "info@crossroadshouse.org",
    services: [
      "Emergency Shelter",
      "Transitional Housing",
      "Supportive Services",
    ],
    description:
      "Shelter and supportive housing for individuals and families on the Seacoast.",
    notes: "Contact for intake; serves the greater Seacoast area.",
  },
  {
    id: 70,
    name: "The Carey House (Salvation Army)",
    category: "Housing",
    subcategory: "Shelters",
    counties: ["All", "Belknap"],
    address: "6 Spring St, Laconia, NH 03246",
    phone: "(603) 528-8086",
    website:
      "https://www.salvationarmyusa.org/nh/laconia/union-avenue-corps/",
    email: "",
    services: ["Emergency Shelter (Singles & Families)"],
    description: "Emergency shelter in Laconia operated by The Salvation Army.",
    notes:
      "Call for bed availability; intake may be 24/7 depending on capacity.",
  },
  {
    id: 71,
    name: "McKenna House (Salvation Army)",
    category: "Housing",
    subcategory: "Shelters",
    counties: ["All", "Merrimack"],
    address: "100 S Fruit St, Concord, NH 03301",
    phone: "(603) 228-3505",
    website:
      "https://easternusa.salvationarmy.org/northern-new-england/concord/provide-shelter/",
    email: "",
    services: ["Emergency Shelter"],
    description:
      "Emergency shelter for adults in Concord operated by The Salvation Army.",
    notes: "Contact directly for intake; singles only.",
  },
  {
    id: 72,
    name: "My Friend's Place",
    category: "Housing",
    subcategory: "Shelters",
    counties: ["All", "Strafford"],
    address: "368 Washington St, Dover, NH 03820",
    phone: "(603) 749-3017",
    website: "https://myfriendsplacenh.org",
    email: "",
    services: ["Emergency Shelter", "Transitional Housing (Families)"],
    description:
      "Emergency shelter in Dover; operates transitional units for families.",
    notes: "Staffed 24/7; call for intake.",
  },
  {
    id: 73,
    name: "Homeless Center for Strafford County (Home for Now)",
    category: "Housing",
    subcategory: "Shelters",
    counties: ["All", "Strafford"],
    address: "202 Washington St, Rochester, NH 03839",
    phone: "(603) 332-3065",
    website: "https://hcscnh.org",
    email: "sheltermanager@homelesscenter.net",
    services: ["Emergency Shelter", "Support Services"],
    description:
      "Emergency shelter and case management for individuals and families.",
    notes:
      "The ‘Home for Now’ shelter operates at the same site; call for intake.",
  },
  {
    id: 74,
    name: "New Generation (Women & Children)",
    category: "Housing",
    subcategory: "Shelters",
    counties: ["All", "Rockingham"],
    address: "568 Portsmouth Ave, Greenland, NH 03840",
    phone: "(603) 436-4989",
    website: "https://newgennh.org",
    email: "office@newgennh.org",
    services: [
      "Shelter for Pregnant Women & Mothers with Children",
      "Transitional Housing",
      "Aftercare/Stabilization",
    ],
    description:
      "Shelter and stabilization services for pregnant women and mothers with children (a program of Catholic Charities NH).",
    notes: "Call for eligibility and intake process.",
  },
  {
    id: 75,
    name: "Friends Program – Emergency Housing",
    category: "Housing",
    subcategory: "Shelters",
    counties: ["All", "Merrimack"],
    address: "30 Thompson St, Concord, NH 03301",
    phone: "(603) 228-1462",
    website: "https://www.friendsprogram.org/emergency-housing",
    email: "",
    services: ["Emergency Shelter (Families)"],
    description: "Family shelter and support services in Concord.",
    notes: "Contact for intake and eligibility.",
  },
  {
    id: 76,
    name: "Brook Street Women’s Shelter",
    category: "Housing",
    subcategory: "Shelters",
    counties: ["All", "Hillsborough"],
    address: "15 Brook St, Manchester, NH 03104",
    phone: "(603) 698-3231",
    website: "",
    email: "",
    services: ["Women’s Emergency Shelter"],
    description: "Women-only, low-barrier emergency shelter in Manchester.",
    notes:
      "No verified current official website is available. Call to confirm that the shelter is operating and to ask about hours and intake.",
  },
  {
    id: 77,
    name: "Burch House (Tri-County CAP)",
    category: "Housing",
    subcategory: "Shelters",
    counties: ["All", "Grafton"],
    address: "Address confidential (Littleton area)",
    phone: "(603) 444-0184",
    website: "https://tccap.org/housing-stability/",
    email: "",
    services: ["Domestic & Sexual Violence Emergency Shelter"],
    description:
      "Confidential emergency shelter for survivors of domestic & sexual violence.",
    notes: "Location withheld for safety; call for services.",
  },
  {
    id: 79,
    name: "Hundred Nights",
    category: "Housing",
    subcategory: "Shelters",
    counties: ["All", "Cheshire"],
    address: "122 Water St, Keene, NH 03431",
    phone: "(603) 352-5197",
    website: "https://hundrednightsinc.org",
    email: "info@hundrednightsinc.org",
    services: ["Emergency Shelter", "Resource Center", "Case Management"],
    description:
      "Low-barrier emergency shelter and year-round resource center.",
    notes:
      "Shelter intake and hours listed on website; email intake contact posted there.",
  },
  {
    id: 80,
    name: "SCS Emergency Shelter (Southwestern Community Services)",
    category: "Housing",
    subcategory: "Shelters",
    counties: ["All", "Cheshire"],
    address: "63 Community Way, Keene, NH 03431",
    phone: "(603) 209-0251",
    website: "https://www.scshelps.org/housing-stabilization-services/",
    email: "scs@scshelps.org",
    services: ["Emergency Shelter", "Coordinated Entry", "Homeless Outreach"],
    description:
      "SCS manages multiple short-term emergency shelters in Cheshire/Sullivan counties with 24/7 access via outreach line.",
    notes: "After hours, call 2-1-1; during day call the outreach line listed.",
  },
  {
    id: 81,
    name: "Southern NH Rescue Mission",
    category: "Housing",
    subcategory: "Shelters",
    counties: ["All", "Hillsborough"],
    address: "40 Chestnut St (men) / 36 Tolles St (women), Nashua, NH 03060",
    phone: "(603) 889-3421",
    website: "https://www.hope4nashua.org",
    email: "director@hope4nashua.org",
    services: [
      "Men’s Emergency Shelter",
      "Women’s Emergency Shelter",
      "Meals",
      "Long-Term Programs",
    ],
    description: "Emergency shelter and programs for men and women in Nashua.",
    notes: "Women’s phone: (603) 521-7127; Men’s phone: (603) 889-3421.",
    contacts: [
      { type: "Women's", phone: "(603) 521-7127" },
      { type: "Men's", phone: "(603) 889-3421" },
    ],
  },
  {
    id: 83,
    name: "Beech Street Winter Shelter",
    category: "Housing",
    subcategory: "Shelters",
    counties: ["All", "Hillsborough"],
    address: "39 Beech St, Manchester, NH 03103",
    phone: "(603) 413-1775",
    website: "",
    email: "",
    services: ["Seasonal Winter Shelter", "Warming Center"],
    description: "Seasonal, low-barrier winter shelter in Manchester.",
    notes:
      "No verified official website is available. Typically operates during winter months; call to confirm opening dates and nightly hours.",
  },
  {
    id: 318,
    name: "New Hampshire Harm Reduction Coalition (NHHRC)",
    category: "Primary SUD Providers",
    statewide: true,
    counties: [
      "All",
      "Hillsborough",
      "Merrimack",
      "Strafford",
      "Rockingham",
      "Carroll",
      "Cheshire",
      "Grafton",
      "Belknap",
    ],
    address: "Statewide network of 11 syringe service programs",
    phone: "",
    website: "https://www.nhhrc.org/",
    email: "info@nhhrc.org",
    services: [
      "Syringe Service Programs (SSPs)",
      "Naloxone/Narcan Distribution and Training",
      "Fentanyl Test Strip Distribution",
      "Overdose Prevention Education",
      "Care Coordination for People Who Use Drugs",
      "Advocacy and Community Education",
      "Gender-Affirming Hormone Therapy Supplies (select sites)",
    ],
    description:
      "The New Hampshire Harm Reduction Coalition operates and supports 11 syringe service programs statewide, providing sterile supplies, safe disposal, naloxone, fentanyl test strips, and care coordination to reduce overdose deaths and prevent transmission of HIV/HCV. NHHRC upholds the inherent worth of all people and promotes compassionate, evidence-based harm reduction strategies.",
    notes:
      "Key SSP locations: Queen City Exchange (Manchester), Concord Exchange (Concord), Hand-Up (Strafford/Rockingham Seacoast), Laconia Exchange (via Navigating Recovery), GROW (Keene Serenity Center), MWV Supports Recovery (Carroll County). All services are free, confidential, and require no ID. Established 2017 following passage of NH SB 234. Visit nhhrc.org/nhssps for the full list of sites and hours.",
  },

  {
    id: 319,
    name: "Community Action for Safe Teens (CAST)",
    category: "Primary SUD Providers",
    counties: ["All", "Hillsborough"],
    address: "56 Mont Vernon St, Milford, NH 03055",
    phone: "(603) 672-1002",
    website: "https://www.svbgc.org/about",
    email: "info@svbgc.org",
    services: [
      "Youth Substance Misuse Prevention",
      "Family and Caregiver Education",
      "Community Coalition Building",
      "Youth Wellness and Healthy-Choice Programs",
    ],
    description:
      "Community Action for Safe Teens is a substance-misuse prevention coalition operating as a committee of the Boys & Girls Club of Souhegan Valley and serving Milford and surrounding Souhegan Valley communities.",
    notes:
      "Prevention-focused program; it does not provide clinical substance-use treatment.",
  },
  {
    id: 13,
    name: "The Doorways",
    category: "Primary SUD Providers",
    statewide: true,
    counties: [
      "All",
      "Belknap",
      "Cheshire",
      "Coös",
      "Grafton",
      "Hillsborough",
      "Merrimack",
      "Strafford",
    ],
    address:
      "9 regional locations across NH (Manchester, Nashua, Concord, Laconia, Dover, Littleton, Berlin, Keene, Lebanon)",
    phone: "Call 2-1-1 (ask for 'Doorway') or local Doorway number",
    website: "https://thedoorway.nh.gov",
    email: "",
    services: [
      "24/7 crisis response for substance use",
      "Clinical evaluation and treatment referral",
      "Medication-assisted treatment (MAT) access",
      "Peer recovery support services",
      "Care coordination and follow-up",
    ],
    description:
      "The Doorway is a statewide initiative with nine hubs providing 24/7 access to SUD treatment, including crisis response, medication-assisted treatment, and recovery supports.",
    notes: "Funded by NH DHHS; part of the State’s hub-and-spoke model.",
  },
  {
    id: 18,
    name: "Health First Family Care Center",
    category: ["Primary SUD Providers", "Primary Mental Health Providers"],
    counties: ["All", "Belknap", "Merrimack", "Grafton"],
    address:
      "841 Central St, Franklin, NH 03235; 22 Strafford St #1, Laconia, NH 03246; 18 Roberts Rd, Canaan, NH 03741",
    phone: "(603) 934-1464",
    website: "https://healthfirstfamily.org/",
    email: "info@healthfirstfamily.org",
    services: [
      "Integrated primary care and behavioral health",
      "Medication-Assisted Treatment (MAT) for opioid use disorder",
      "Counseling and case management",
      "Preventive and chronic care",
    ],
    description:
      "Federally Qualified Health Center providing integrated primary care, mental health, and substance use disorder treatment including MAT.",
    notes: "Sliding fee scale; accepts Medicaid, Medicare, and most insurance.",
  },
  {
    id: 19,
    name: "Northern Human Services",
    category: ["Primary SUD Providers", "Primary Mental Health Providers"],
    counties: ["All", "Carroll", "Coös", "Grafton"],
    address:
      "87 Washington St, Conway, NH 03818 (administrative office; multiple local centers)",
    phone: "(603) 447-3347",
    website: "https://northernhs.org",
    email: "info@northernhs.org",
    services: [
      "Community mental health and SUD treatment",
      "Outpatient counseling and therapy",
      "Medication-Assisted Treatment (MAT)",
      "Case management and support services",
    ],
    description:
      "Community mental health and SUD provider for northern NH counties, offering outpatient counseling, MAT, and support services.",
    notes:
      "Multiple regional clinics: Berlin, Colebrook, Littleton, North Conway, Wolfeboro, etc.",
  },
  {
    id: 20,
    name: "Road to a Better Life",
    category: "Primary SUD Providers",
    counties: [
      "All",
      "Carroll",
      "Cheshire",
      "Coös",
      "Grafton",
      "Merrimack",
      "Strafford",
      "Sullivan",
    ],
    address:
      "Main office: 255 Route 108, Somersworth, NH 03878 (with additional locations in Concord, Keene, Lebanon, Littleton, Merrimack, Plymouth)",
    phone: "(603) 841-2301",
    website: "",
    email: "",
    services: [
      "Medication-Assisted Treatment (Suboxone, Sublocade, Vivitrol)",
      "Counseling and recovery support",
      "Outpatient opioid and alcohol use disorder care",
    ],
    description:
      "Private outpatient SUD treatment practice specializing in MAT for opioid and alcohol use disorders across multiple NH locations.",
    notes:
      "Accepts Medicaid, Medicare, and commercial insurance; new patients often accepted quickly. The former website no longer resolves; recheck for a verified official replacement.",
  },
  {
    id: 22,
    name: "Horizons Counseling Center",
    category: "Primary SUD Providers",
    counties: ["All", "Belknap", "Grafton"],
    address:
      "61 Beacon Street West, Suite 3, Laconia, NH 03246 (also Plymouth & Franklin sites)",
    phone: "(603) 524-4664",
    website: "https://horizonsnh.org/",
    email: "",
    services: [
      "Outpatient substance use counseling",
      "DWI/Impaired Driver Care Management Program (IDCMP)",
      "Adolescent and family therapy",
      "Anger management & related behavioral health programs",
    ],
    description:
      "Community-based outpatient SUD and mental health counseling provider in central NH, including state-licensed DWI program.",
    notes: "Use the official website's contact form or call for current locations, insurance, sliding-fee eligibility, and appointment availability.",
  },
  {
    id: 23,
    name: "Narcotics Anonymous (NA) – New Hampshire",
    category: ["Primary SUD Providers", "Online Resources and Support Groups"],
    statewide: true,
    counties: [
      "All",
      "Belknap",
      "Carroll",
      "Cheshire",
      "Coös",
      "Grafton",
      "Hillsborough",
      "Merrimack",
      "Rockingham",
      "Strafford",
      "Sullivan",
    ],
    address: "Meetings statewide; NA helpline covers all NH regions",
    phone: "24/7 Helpline: (833) 436-6166",
    website: "https://gsana.org/meeting-list/",
    email: "",
    services: [
      "Peer-led 12-step recovery meetings",
      "Statewide meeting locator",
      "Helpline support",
    ],
    description:
      "NA is a peer-led recovery fellowship for individuals with substance use disorder, offering 12-step meetings throughout NH.",
    notes:
      "Meetings are free, open to anyone seeking recovery; in-person and virtual options.",
  },
  {
    id: 24,
    name: "Alcoholics Anonymous (AA) – New Hampshire",
    category: ["Primary SUD Providers", "Online Resources and Support Groups"],
    statewide: true,
    counties: [
      "All",
      "Belknap",
      "Carroll",
      "Cheshire",
      "Coös",
      "Grafton",
      "Hillsborough",
      "Merrimack",
      "Rockingham",
      "Strafford",
      "Sullivan",
    ],
    address: "NH State Service Office: 1330 Hooksett Rd, Hooksett, NH 03106",
    phone: "NHAA Helpline: (800) 593-3330; Service Office: (603) 622-6967",
    website: "https://nhaa.net/meetings/",
    email: "office@nhaa.net",
    services: [
      "Peer-led 12-step recovery meetings",
      "Hotline and support network",
      "Meeting locator and group resources",
    ],
    description:
      "AA is a peer-led recovery fellowship for individuals seeking sobriety from alcohol, offering meetings statewide and support resources through NH Area Assembly.",
    notes: "Meetings are free, widely available, and run daily statewide.",
  },
  {
    id: 463,
    name: "Recovery Dharma at Whole Village – Plymouth",
    category: ["Primary SUD Providers", "Online Resources and Support Groups"],
    statewide: false,
    counties: ["Grafton"],
    address: "Whole Village Family Resource Center, 258 Highland St, Plymouth, NH 03264",
    phone: "",
    website: "https://recoverydharma.org/meetings/rd-whole-village/",
    email: "kmh04330@yahoo.com",
    services: [
      "In-person peer-led recovery meeting",
      "Meditation and mindfulness practice",
      "Open discussion and shared experience",
      "Youth-welcoming and wheelchair-accessible meeting",
    ],
    description:
      "Recovery Dharma is a peer-led approach to recovery from addiction and compulsive behaviors that draws on Buddhist practices, meditation, compassion, and community. Buddhist identity and prior meditation experience are not required.",
    notes:
      "New weekly meeting begins Tuesday, November 3, 2026, and meets Tuesdays from 6:00–7:00 PM. Open to members and non-members; enter through the Whole Village front doors and follow signs.",
  },

  // Primary SUD Providers - Respite subcategory
  {
    id: 86,
    name: "Nashua Respite (NH Respite – GH Recovery Solutions)",
    category: "Primary SUD Providers",
    subcategory: "Respite",
    counties: ["All", "Hillsborough"],
    address: "63 Temple St, Nashua, NH 03060",
    phone: "Dial 2-1-1 for help • Business inquiries: (603) 521-8546",
    website: "https://nhrespite.com",
    email: "info@nhrespite.com",
    services: [
      "Respite Care",
      "Crisis Support",
      "Stabilization (Doorway bridge)",
    ],
    description:
      "Doorway-supported after-hours site providing supervised, short-term stays with separate male/female spaces (26 beds) until The Doorway opens the next business day.",
    notes:
      "Hours posted as Mon–Fri 5pm–8am, Sat–Sun 24 hours; for immediate help, call 2-1-1; the listed phone is for business inquiries only.",
  },

  // Primary SUD Providers - Detox subcategory
  {
    id: 87,
    name: "Avenues (Avenues Recovery Center at Dublin)",
    category: "Primary SUD Providers",
    subcategory: "Detox",
    counties: ["All", "Cheshire"],
    address: "3 Pierce Rd, Dublin, NH 03444",
    phone: "(603) 317-5600",
    website:
      "https://www.avenuesrecovery.com/locations/dublin-nh-drug-rehab-center/",
    email: "AdmissionsNH@avenuesrecovery.com",
    services: [
      "Medical Detox",
      "Withdrawal Management",
      "Residential/Inpatient",
    ],
    description:
      "Medical detox and residential addiction treatment on a secluded Dublin campus with 24/7 medical care and dual-diagnosis support.",
    notes:
      "Local admissions line; accepts many commercial plans and NH Medicaid partners.",
  },
  {
    id: 88,
    name: "NH Detox (New Hampshire Detox Center – TruHealing)",
    category: "Primary SUD Providers",
    subcategory: "Detox",
    counties: ["All", "Grafton"],
    address: "2957 Main St, Bethlehem, NH 03574",
    phone: "(833) 649-1053",
    website: "https://www.nh-detox.com/",
    email: "",
    services: ["Medical Detox", "Withdrawal Management", "Residential"],
    description:
      "Non-hospital, limited withdrawal-management medical detox with individualized care and MAT options.",
    notes:
      "Operated under TruHealing; official site lists (833) 649-1053 as the contact number.",
  },
  {
    id: 89,
    name: "Liberty (Liberty Health Services)",
    category: "Primary SUD Providers",
    subcategory: ["Detox", "Treatment Centers"],
    counties: ["All", "Rockingham"],
    address: "29 Ashleigh Dr, Derry, NH 03038",
    phone: "(603) 691-1130",
    website: "https://libertyhealthdetox.com/",
    email: "admissions@libertyhealthdetox.com",
    servicesGuide: "https://online.flippingbook.com/view/935663476/",
    services: [
      "Medical Detox",
      "Withdrawal Management",
      "Residential/Inpatient",
    ],
    description:
      "Private, medically monitored detox with 24/7 supervision; transitions to residential and step-down levels of care.",
    notes:
      "Local facility number shown; national admissions line sometimes listed as (855) 689-5685.",
  },
  {
    id: 91,
    name: "Green Mountain Treatment Center",
    category: "Primary SUD Providers",
    subcategory: ["Detox", "Treatment Centers"],
    counties: ["All", "Carroll"],
    address: "244 High Watch Rd, Effingham, NH 03882",
    phone: "(855) 712-7784",
    website:
      "https://www.graniterecoverycenters.com/treatment-centers/green-mountain-treatment-center/",
    email: "",
    services: [
      "Medical Detox",
      "Withdrawal Management",
      "Residential Treatment",
      "Partial Hospitalization (PHP)",
      "Intensive Outpatient (IOP)",
    ],
    description:
      "Residential substance-use treatment campus offering on-site medical detox and a continuum that includes residential treatment, PHP, and IOP.",
    notes:
      "Granite Recovery Centers campus. Contact admissions to confirm current eligibility, insurance coverage, and level-of-care availability.",
  },
  {
    id: 92,
    name: "Plymouth House (Guardian Recovery – The Plymouth House)",
    category: "Primary SUD Providers",
    subcategory: ["Detox", "Treatment Centers"],
    counties: ["All", "Grafton"],
    address: "446 Main St, Plymouth, NH 03264",
    phone: "(888) 693-1927",
    website: "https://www.theplymouthhouse.com/",
    email: "info@theplymouthhouse.com",
    services: [
      "Medical Detox",
      "Withdrawal Management",
      "Residential/Inpatient",
    ],
    description:
      "Medical detox integrated with a 12-Step–centered residential program supported by clinical and psychiatric care.",
    notes:
      "Part of Guardian Recovery Network; accepts NH Medicaid plans per site.",
  },
  {
    id: 93,
    name: "Live Free (Live Free Recovery – Keene Detox & Inpatient)",
    category: "Primary SUD Providers",
    subcategory: "Detox",
    counties: ["All", "Cheshire"],
    address: "881 Marlboro St, Keene, NH 03431",
    phone: "(888) 527-1508",
    website:
      "https://livefreerecoverynh.com/locations/detox-inpatient-rehab-keene-nh/",
    email: "lfrsadmissions@livefreerecoverynh.com",
    services: [
      "Medical Detox",
      "Withdrawal Management",
      "Residential/Inpatient",
    ],
    description:
      "Licensed medical detox with 24/7 clinical supervision; step-down to residential and outpatient levels of care.",
    notes: "NH Medicaid accepted; also operates programs in Manchester, NH.",
  },

  // Primary SUD Providers - Treatment Centers subcategory
  {
    id: 25,
    name: "Brighter Day Recovery",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["All", "Strafford"],
    address: "362 Route 108, Somersworth, NH 03878",
    phone: "(603) 699-9000",
    website: "https://brighterdayrecovery.com/",
    email: "info@brighterdayrecovery.com",
    services: [
      "Consultation & clinical assessment",
      "Outpatient addiction treatment",
      "Individual therapy",
      "Group support therapy",
      "Family education & support",
      "Ongoing recovery support",
      "Evidence-based & holistic therapies",
      "Insurance benefits verification assistance",
    ],
    description:
      "Outpatient addiction treatment center in Somersworth, NH providing individualized therapy, group sessions, family support, and ongoing recovery services.",
    notes:
      "Site states they accept most major insurance (benefits verified by admissions). Future inpatient program planned; outpatient services active now.",
  },

  {
    id: 94,
    name: "Avenues Recovery (Dublin & Concord)",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["All", "Cheshire", "Merrimack"],
    address: "3 Pierce Rd, Dublin, NH 03444",
    phone: "(603) 317-5600",
    website:
      "https://www.avenuesrecovery.com/locations/new-hampshire-drug-rehab-center/",
    email: "AdmissionsNH@avenuesrecovery.com",
    services: [
      "Medical Detox",
      "Residential Treatment",
      "Partial Hospitalization (PHP)",
      "Intensive Outpatient (IOP)",
      "Outpatient Counseling",
      "Medication-Assisted Treatment (MAT)",
    ],
    description:
      "Detox & residential campus in Dublin with additional outpatient treatment (PHP/IOP) delivered at Avenues Recovery Center at New England in Concord.",
    notes:
      "Concord outpatient site: 2 Chenell Dr, Concord, NH 03301, phone (603) 505-8365.",
    contacts: [
      { type: "Dublin (Detox/Residential)", phone: "(603) 317-5600" },
      { type: "Concord (Outpatient)", phone: "(603) 505-8365" },
    ],
  },
  {
    id: 95,
    name: "Paugus Bay Behavioral Health",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["All", "Belknap"],
    address: "745 N. Main St, Laconia, NH 03246",
    phone: "",
    website: "https://paugusbaybh.com/",
    email: "",
    services: [
      "Outpatient SUD Treatment",
      "Partial Hospitalization (PHP)",
      "Intensive Outpatient (IOP)",
      "Telehealth",
    ],
    description:
      "Outpatient substance use and mental health services for adults and adolescents.",
    notes: "Use website contact form; program phones vary by service.",
  },
  {
    id: 100,
    name: "Headrest",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["All", "Grafton"],
    address: "14 Church St, Lebanon, NH 03766",
    phone: "(603) 448-4872",
    website: "https://headrest.org/",
    email: "",
    services: [
      "Outpatient Counseling",
      "Crisis Line (24/7)",
      "Recovery Support",
    ],
    description:
      "Community nonprofit providing SUD counseling and a 24/7 crisis hotline.",
    notes: "Call main line for clinical services and referrals.",
  },
  {
    id: 102,
    name: "The Granite House",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["All", "Rockingham"],
    address: "35 W Broadway, Derry, NH 03038",
    phone: "(855) 712-7784",
    website:
      "https://www.graniterecoverycenters.com/treatment-centers/the-granite-house/",
    email: "",
    services: ["Residential Treatment"],
    description: "Residential addiction treatment (Granite House brand).",
    notes: "Contact for current bed availability and exact street address.",
  },
  {
    id: 103,
    name: "GH Recovery Solutions",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["All", "Hillsborough"],
    address: "491 Amherst St, Unit 105, Nashua, NH 03063",
    phone: "(888) 345-2025",
    website: "https://www.ghrecovery.com/about-us/",
    email: "info@ghrecovery.com",
    services: [
      "Outpatient Treatment",
      "Partial Hospitalization (PHP)",
      "Intensive Outpatient (IOP)",
      "Medication-Assisted Treatment (MAT)",
    ],
    description: "Outpatient SUD treatment with higher levels of care.",
    notes: "Located on Amherst St in Nashua.",
  },
  {
    id: 104,
    name: "Riverbend Community Mental Health – Addiction Services",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["All", "Merrimack"],
    address: "111 Pleasant St, Concord, NH 03301",
    phone: "(603) 228-1600",
    website: "https://www.riverbendcmhc.org/",
    email: "",
    services: [
      "Outpatient SUD Treatment",
      "Medication-Assisted Treatment (Suboxone)",
      "Integrated Behavioral Health",
    ],
    description:
      "Community mental health center offering SUD treatment and MAT.",
    notes: "Call for intake and program details.",
  },
  {
    id: 105,
    name: "Live Free Recovery (Manchester & Keene)",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["All", "Hillsborough", "Cheshire"],
    address:
      "500 Commercial St, Manchester, NH 03101; 21 West St, Keene, NH 03431",
    phone: "(888) 705-3759",
    website: "https://livefreerecoverynh.com/",
    email: "",
    services: [
      "Medical Detox",
      "Residential Treatment",
      "Partial Hospitalization (PHP)",
      "Intensive Outpatient (IOP)",
      "Outpatient",
      "Sober Living",
    ],
    description:
      "Multiple levels of care with locations in Manchester and Keene.",
    notes: "Admissions via toll-free line.",
  },
  {
    id: 106,
    name: "Bonfire Behavioral Health",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["All", "Strafford"],
    address: "35 E Industrial Way, Suite 11, Rochester, NH 03867",
    phone: "(603) 617-4815",
    website: "https://www.bonfirerecovery.com/",
    email: "",
    services: [
      "Outpatient Treatment",
      "Intensive Outpatient (IOP)",
      "Sober Living (affiliated)",
    ],
    description:
      "Outpatient and IOP services; recovery housing options affiliated.",
    notes: "",
  },
  {
    id: 108,
    name: "Clear Steps Recovery",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["All", "Rockingham"],
    address: "1D Commons Dr, Units 21-22, Londonderry, NH 03053",
    phone: "(603) 769-8981",
    website: "https://clearstepsrecovery.com/",
    email: "",
    services: [
      "Outpatient Treatment",
      "Intensive Outpatient (IOP)",
      "Virtual Care",
      "Medication-Assisted Treatment (MAT)",
    ],
    description: "Outpatient SUD care with virtual and in-person options.",
    notes: "",
  },
  {
    id: 109,
    name: "Hope on Haven Hill",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["All", "Strafford"],
    address: "Rochester & Somersworth, NH (multiple locations)",
    phone: "(603) 841-5353",
    website: "https://www.hopeonhavenhill.org/",
    email: "",
    services: [
      "Residential Treatment (Women & Perinatal)",
      "Outpatient Care",
      "Family Support",
    ],
    description:
      "Specialized treatment for pregnant and parenting women; residential and outpatient programs.",
    notes: "See website for location-specific contacts.",
  },
  {
    id: 110,
    name: "Cynthia Day Family Center (Harbor Care)",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["All", "Hillsborough"],
    address: "615 Amherst St, Nashua, NH 03063",
    phone: "(603) 881-4848",
    website: "https://harborcarenh.org/healthcare/substance-use/",
    email: "cdfcintake@harborcarenh.org",
    services: [
      "Residential substance use treatment",
      "Pregnant and parenting women’s services",
      "Family-centered treatment",
      "Clinical and recovery support",
    ],
    description:
      "Residential substance use treatment for pregnant and parenting women, with accommodations for children up to age 12.",
    notes: "Call for eligibility and intake process.",
  },
  {
    id: 111,
    name: "Turning Point (SENHS / Community Partners)",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["All", "Strafford"],
    address: "272 County Farm Rd, Dover, NH 03820",
    phone: "(603) 516-9300",
    website: "https://senhs.org/programs/residential/",
    email: "TurningPoint@communitypartnersnh.org",
    services: ["Residential (Halfway House)", "Recovery Support"],
    description: "Southeastern NH Services transitional residential program.",
    notes: "Contact SENHS for current admissions.",
  },
  {
    id: 112,
    name: "A Better State",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["All", "Hillsborough"],
    address: "41 Sagamore Park Rd, Hudson, NH 03051",
    phone: "(781) 412-1488",
    website: "",
    email: "",
    services: ["Outpatient Addiction Treatment"],
    description: "Outpatient SUD treatment provider in Hudson.",
    notes:
      "No verified official website is available. Phone and address were confirmed through public directories; call before visiting.",
  },
  {
    id: 113,
    name: "Bridge Street Recovery",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["All", "Hillsborough"],
    address: "15 Monument Sq, Bennington, NH 03442",
    phone: "(603) 255-7070",
    website: "",
    email: "",
    services: ["Outpatient Treatment", "Sober Living (on-site)"],
    description: "Outpatient SUD treatment with optional on-site sober living.",
    notes:
      "No verified current official website is available. The former domain is no longer controlled by the provider; call to confirm current operating status and services.",
  },
  {
    id: 114,
    name: "GateHouse Treatment – Nashua",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["All", "Hillsborough"],
    address: "491 Amherst St, Suite 105, Nashua, NH 03063",
    phone: "(855) 448-3588",
    website:
      "https://www.gatehousetreatment.com/rehab-locations/nashua-new-hampshire/",
    email: "",
    services: ["Outpatient Treatment", "Case Management", "Aftercare/Alumni"],
    description:
      "GateHouse’s New Hampshire location; outpatient-focused services.",
    notes: "Call to confirm current levels of care offered on-site.",
  },
  {
    id: 115,
    name: "Renew Health + Recovery – New Hampshire",
    category: "Primary SUD Providers",
    statewide: true,
    subcategory: "Treatment Centers",
    counties: ["All", "Hillsborough"],
    address: "15 Constitution Dr, Suite 103, Bedford, NH 03110",
    phone: "(603) 769-8462",
    website: "https://www.renewhealthrecovery.com/where-we-serve/new-hampshire/",
    email: "",
    services: [
      "In-Home Addiction Treatment (IHAT)",
      "Outpatient/Virtual",
      "Care Coordination",
    ],
    description: "Statewide in-home addiction treatment with a multidisciplinary care team, formerly Aware Recovery Care.",
    notes: "Statewide service; Bedford office is administrative.",
  },
  {
    id: 116,
    name: "RISE Recovery Services (Mid-State Health Center)",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["All", "Grafton"],
    address: "28 Main St, Plymouth, NH 03264",
    phone: "(603) 217-6300",
    website: "https://www.midstatehealth.org/service/riserecovery/",
    email: "",
    services: ["Recovery Coaching", "Peer Support", "Groups", "Navigation"],
    description:
      "Peer recovery support services offered by Mid-State Health Center.",
    notes: "Satellite support available in Lincoln and Bristol.",
  },
  {
    id: 117,
    name: "Recovery Clinic (Concord Hospital – Laconia/Franklin)",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["All", "Belknap"],
    address: "80 Highland St, Laconia, NH 03246",
    phone: "(603) 737-6047",
    website: "https://www.concordhospital.org/services/recovery-clinic/",
    email: "",
    services: [
      "Medication-Assisted Treatment (MAT)",
      "Outpatient SUD Care",
      "Harm Reduction & Linkage",
    ],
    description:
      "Hospital-based recovery clinic providing MAT and outpatient care.",
    notes: "Clinics at Concord Hospital – Laconia and Franklin.",
  },
  {
    id: 118,
    name: "Capital Recovery Health",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["All", "Merrimack"],
    address: "1 Fisher Rd., Boscawen, NH 03303",
    phone: "(603) 207-4814",
    website: "https://capitalrecoveryhealth.com",
    email: "",
    services: [
      "Addiction Treatment (Opioids, Alcohol, Meth, Cocaine, Prescription Misuse)",
      "Partial Hospitalization Program (PHP)",
      "Holistic & Trauma-Informed Therapies",
      "Family Therapy & Support",
      "CBT-Based Clinical Care",
    ],
    description:
      "Located in Boscawen, NH, Capital Recovery Health provides personalized, holistic addiction treatment in a tranquil, farmhouse-style setting. Their Partial Hospitalization Program complements outpatient services and includes family involvement and trauma-informed care.",
    notes:
      "Certified as a Recovery Friendly Workplace in Concord. Staff includes licensed clinicians, medical providers, and recovery coaches. (See “Recovery Friendly Workplace” program affiliation.)",
  },
  {
    id: 119,
    name: "One Peer to Another (Peer Recovery Center)",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["All", "Belknap"],
    address: "328 Union Ave, Laconia, NH 03246",
    phone: "(603) 528-7742",
    website: "https://onepeer.org/",
    email: "",
    services: ["Peer Recovery Support", "Groups", "Recovery Coaching"],
    description:
      "Peer-led recovery community organization serving the Lakes Region.",
    notes: "",
  },
  {
    id: 120,
    name: "Better Life Partners",
    category: "Primary SUD Providers",
    statewide: true,
    subcategory: "Treatment Centers",
    counties: ["All"],
    address: "Statewide (community & telehealth)",
    phone: "(866) 679-0831",
    website: "https://betterlifepartners.com/",
    email: "",
    services: [
      "Medication-Assisted Treatment (Suboxone)",
      "Counseling & Groups",
      "Care Navigation",
    ],
    description:
      "Low-barrier MAT and recovery supports offered virtually and in local communities across NH.",
    notes: "Same-day starts often available; accepts many insurances.",
  },
  {
    id: 121,
    name: "Granite Recovery Centers (Network)",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["All", "Carroll", "Merrimack", "Rockingham"],
    address: "Statewide network (Effingham, Canterbury, Derry area)",
    phone: "(855) 918-0788",
    website: "https://www.graniterecoverycenters.com/",
    email: "",
    services: [
      "Medical Detox",
      "Residential Treatment",
      "PHP",
      "IOP",
      "Outpatient",
    ],
    description: "Network including Green Mountain Treatment Center, Canterbury Behavioral Health, New Freedom Academy in Salem, and The Granite House.",
    notes: "Admissions via central GRC line.",
  },
  {
    id: 122,
    name: "The Process Recovery Center",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["All", "Hillsborough"],
    address: "21 Factory St, Nashua, NH 03060",
    phone: "(888) 649-1149",
    website: "https://www.theprocessrecoverycenter.com/",
    email: "",
    services: [
      "Outpatient Treatment",
      "Intensive Outpatient (IOP)",
      "Medication-Assisted Treatment (MAT)",
      "Aftercare",
    ],
    description:
      "Outpatient-focused program offering multiple evidence-based modalities and MAT.",
    notes: "Additional site listed at 33 East Pearl St in directories.",
  },
  {
    id: 123,
    name: "Addiction Recovery Services (ARS)",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["All", "Rockingham"],
    address: "Greenland, NH (Seacoast location)",
    phone: "(978) 228-5853",
    website: "https://arsnh.com/",
    email: "admissions@arsnh.com",
    services: [
      "Intensive Outpatient (IOP)",
      "Outpatient",
      "Psychiatric Medication Management",
      "Family Education",
    ],
    description:
      "Evidence-based IOP and outpatient services from a Seacoast NH location.",
    notes: "Serves NH/MA/ME; call/text for intake.",
  },
  {
    id: 125,
    name: "Adult & Teen Challenge New Hampshire (Men’s Center)",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["All", "Hillsborough"],
    address: "147 Laurel St, Manchester, NH 03103",
    phone: "(603) 647-7770",
    website: "https://atcnortheast.org/new-hampshire-mens-center/",
    email: "",
    services: [
      "Faith-Based Residential Program",
      "Life Skills/Vocational Training",
      "Aftercare/Alumni",
    ],
    description: "Long-term, faith-based residential program for adult men.",
    notes: "Contact for program length, cost, and eligibility.",
  },
  {
    id: 150,
    name: "Chrysalis Recovery Center LLC / CRC-IDCMP",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["All", "Merrimack", "Rockingham"],
    address: "112 S. State St, Concord, NH 03301",
    satellite_offices: [
      "20 Canal St, Franklin, NH 03235 (Franklin Business Center – upstairs)",
      "1130 1st NH Turnpike, Northwood, NH 03264 (Route 4)",
    ],
    phone: "(603) 998-4210",
    fax: "(603) 532-0720",
    website: "https://www.chrysalisrecovery.com/",
    email: "intake@crc-idcmp.com",
    services: [
      "Individual counseling (in-person, telehealth, or phone)",
      "Alcohol & drug evaluations (LADC/MLADC)",
      "SAP evaluations for DOT drivers",
      "Anger management counseling & evaluations",
      "Mental & behavioral health counseling",
      "IDCMP – Impaired Driver Care Management Program",
    ],
    description:
      "Chrysalis Recovery Center is a counseling and Impaired Driver Care Management Program (IDCMP) serving New Hampshire since 2009. Staffed by licensed clinicians and certified specialists, Chrysalis provides evaluations, counseling, and court-mandated programs including IDCMP, IDSP, anger management, and SAP evaluations for DOT compliance.",
    notes:
      "In-person locations in Concord (Merrimack County), Franklin (Merrimack County), and Northwood (Rockingham County). Accepts Medicaid and major insurers (Ambetter, AmeriHealth Caritas, BCBS, Cigna, NH Healthy Families, WellSense); self-pay accepted.",
  },

  // Primary SUD Providers - RCO subcategory
  {
    id: 126,
    name: "Navigating Recovery of the Lakes Region",
    category: "Primary SUD Providers",
    subcategory: "RCO",
    counties: ["All", "Belknap"],
    address: "102 Court St, Laconia, NH 03246",
    phone: "(603) 524-5939",
    website: "https://www.navigatingrecovery.org",
    email: "",
    services: [
      "Peer Recovery Coaching",
      "Meetings & Groups",
      "Harm Reduction (Laconia Exchange)",
      "Family Support",
      "Resource Navigation",
    ],
    description:
      "Peer-led Recovery Community Organization serving the Lakes Region with coaching, groups, harm reduction, and family supports.",
    notes:
      "Hours: Mon/Tue/Thu/Fri 9–5; Wed 9–4; Sat 9–1. For after-hours support, call 2-1-1.",
  },
  {
    id: 127,
    name: "ARCHWAYS",
    category: "Primary SUD Providers",
    subcategory: "RCO",
    counties: ["All", "Belknap", "Merrimack", "Grafton"],
    address: "Multiple sites (Tilton, Franklin, Concord, Plymouth), NH",
    phone: "(603) 286-4255",
    website: "https://www.archwaysnh.org",
    email: "",
    services: [
      "Peer Recovery Coaching",
      "Telephone Recovery Support",
      "Mutual Aid Meetings",
      "Employment Support",
      "Family Support",
      "Recovery Friendly Workplace",
    ],
    description:
      "Multi-site RCO offering peer recovery services, family programs, meetings, and employer supports across the region.",
    notes:
      "Locations & phones: Tilton (603) 286-4255 • Franklin (603) 671-0060 • Concord (603) 671-0300 • Plymouth (603) 346-4477.",
  },
  {
    id: 128,
    name: "HOPE for NH Recovery",
    category: "Primary SUD Providers",
    subcategory: "RCO",
    counties: ["All", "Hillsborough"],
    address: "293 Wilson St, Suite 101, Manchester, NH 03103",
    phone: "(603) 935-7524",
    website: "https://recoverynh.org",
    email: "info@recoverynh.org",
    services: [
      "Peer Recovery Coaching",
      "Meetings & Support Groups",
      "Telephone/Drop-in Support",
      "Family & Community Programs",
    ],
    description:
      "Manchester-based RCO providing peer-led recovery supports and meetings for all pathways of recovery.",
    notes: "Typical hours: Mon–Fri 11:30am–9pm; Sat 12–9pm (closed Sun).",
  },
  {
    id: 130,
    name: "White Horse Recovery",
    category: "Primary SUD Providers",
    subcategory: "RCO",
    counties: ["All", "Carroll", "Coös", "Grafton"],
    address:
      "Main RCO site: 32 NH-16B (Indian Mound Plaza), Center Ossipee, NH 03814",
    phone: "(603) 651-1441",
    website: "https://whitehorserecovery.org",
    email: "",
    services: [
      "Peer Recovery Coaching",
      "Harm Reduction & Naloxone",
      "Mutual Support Meetings",
      "Resource Referral",
      "Walk-in Recovery Center (The Shed)",
    ],
    description:
      "Network of RCOs serving the NH North Country & Mt. Washington Valley with peer support, meetings, and harm reduction.",
    notes:
      "Multiple locations (Center Ossipee, North Conway, Berlin, Littleton). Call for nearest center/hours.",
  },
  {
    id: 131,
    name: "MWV Supports Recovery Coalition",
    category: "Primary SUD Providers",
    subcategory: "RCO",
    counties: ["All", "Carroll"],
    address: "1620 E Main St, Center Conway, NH 03818",
    phone: "(603) 662-0668",
    website: "https://www.mwvsupportsrecovery.org",
    email: "mwvaddictionresource@gmail.com",
    services: [
      "Peer Recovery Coaching",
      "Meetings & Groups",
      "Telephone Recovery Support",
      "Resource Navigation",
      "Community Events",
    ],
    description:
      "Community Resource Center (RCO) offering peer support, groups, and recovery navigation in the Mt. Washington Valley.",
    notes: "See site for program calendar and updates.",
  },
  {
    id: 132,
    name: "Keene Serenity Center",
    category: "Primary SUD Providers",
    subcategory: "RCO",
    counties: ["All", "Cheshire"],
    address: "24 Vernon St, Keene, NH 03431",
    phone: "(603) 283-5015",
    website: "https://www.kscrecovery.org",
    email: "info@kscrecovery.org",
    services: [
      "Peer Recovery Coaching",
      "Meetings & Groups",
      "Transportation (Road to Recovery)",
      "Harm Reduction (programming/hours vary)",
      "Resource Navigation",
    ],
    description:
      "RCO in Keene providing coaching, groups, transportation assistance, and harm reduction services.",
    notes:
      "Open Mon–Fri 9am–5pm; additional meetings/events outside regular hours.",
  },
  {
    id: 133,
    name: "SOS Recovery Community Organization",
    category: "Primary SUD Providers",
    subcategory: "RCO",
    counties: ["All", "Strafford", "Rockingham"],
    address: "4 Broadway, Dover, NH 03820 (see notes for other sites)",
    phone: "(603) 841-2350",
    website: "https://sosrco.org",
    email: "info@sosrco.org",
    services: [
      "Peer Recovery Coaching",
      "Telephone Recovery Support",
      "Family Programs",
      "Harm Reduction Services",
      "Advocacy & Trainings",
      "Meetings & Workshops",
    ],
    description:
      "Peer-led RCO with centers in Dover, Rochester, and Exeter providing coaching, harm reduction, family supports, and trainings.",
    notes:
      "Locations: Dover (4 Broadway) • Rochester (14 Signal St) • Exeter (92 Portsmouth Ave, Suite 10). Hours vary by site.",
  },
  {
    id: 134,
    name: "Revive Recovery Resource Center",
    category: "Primary SUD Providers",
    subcategory: "RCO",
    counties: ["All", "Hillsborough", "Rockingham"],
    address: "Headquarters: 263 Main St, Nashua, NH 03060",
    phone: "(888) 317-8312",
    website: "https://www.reviverecovery.org",
    email: "info@reviverecovery.org",
    services: [
      "Peer Recovery Coaching",
      "Harm Reduction",
      "Meetings & Groups",
      "Resource Navigation",
    ],
    description: "Peer recovery support organization serving Nashua, Manchester, Derry, and Milford. The Milford center continues the work of the Addiction Recovery Coalition of New Hampshire (ARCNH), which has joined Revive Recovery.",
    notes: "Locations: Nashua, 263 Main St; Manchester, 99 Blaine St; Derry Friendship Center, 6 Railroad Ave; Milford (formerly ARCNH), 180 Elm St. Call the central number or check the locations page for current hours and available services.",
  },
  {
    id: 135,
    name: "Safe Harbor Recovery Center (Granite Pathways)",
    category: "Primary SUD Providers",
    subcategory: "RCO",
    counties: ["All", "Rockingham"],
    address: "115 Heritage Ave, Portsmouth, NH 03801",
    phone: "(603) 570-9444",
    website: "https://granitepathwaysnh.org/safe-harbor-recovery-center-1/",
    email: "info@granitepathways.org",
    services: [
      "Peer Recovery Coaching",
      "Telephone Recovery Support",
      "Meetings & Workshops",
      "Family Support",
      "Reentry/Justice Partnerships",
    ],
    description:
      "Accredited RCO (CAPRSS) offering peer recovery services, education, and family supports on the Seacoast.",
    notes:
      "Open weekdays from 8:30am through evening programming; Saturdays 8:30am–2pm. Director contact available on site.",
  },
  {
    id: 136,
    name: "TLC Family Resource Center – Center for Recovery Resources (CRR)",
    category: "Primary SUD Providers",
    subcategory: "RCO",
    counties: ["All", "Sullivan", "Grafton"],
    address:
      "Claremont: 62 Pleasant St, Claremont, NH 03743; Lebanon: 24 Hanover St, Suite 2, Lebanon, NH 03766",
    phone: "(603) 542-1848 (Claremont); (603) 790-3779 (Lebanon)",
    website: "https://www.tlcfamilyrc.org/recovery-resources.html",
    email: "",
    services: [
      "Peer Recovery Support",
      "Recovery Coaching",
      "Groups & Workshops",
      "Resource Navigation",
      "Family Support",
    ],
    description:
      "TLC’s Center for Recovery Resources (CRR) offers peer-based supports and recovery services in Sullivan and Grafton counties.",
    notes:
      "See site for program calendar and contact details for each location.",
  },

  // Primary SUD Providers - PSC subcategory
  {
    id: 137,
    name: "Alternative Life Center (North Country PSA – Conway)",
    category: "Primary SUD Providers",
    subcategory: "PSC",
    counties: ["All", "Carroll"],
    address: "6 Main St, Conway, NH 03818",
    phone: "(603) 447-1765",
    website: "https://www.northcountrypsa.org/",
    email: "alcjodicollins@gmail.com",
    services: [
      "Peer Support",
      "Daily Recovery Activities",
      "Individual Support",
      "Warmline",
    ],
    description:
      "North Country Peer Support’s Conway site providing peer-run support and a nightly warmline.",
    notes: "Warmline: (866) 447-1765 (evenings).",
  },
  {
    id: 138,
    name: "Connections Peer Support Center",
    category: "Primary SUD Providers",
    subcategory: "PSC",
    counties: ["All", "Rockingham"],
    address: "544 Islington St, Portsmouth, NH 03801",
    phone: "(603) 427-6966",
    website: "https://connectionspeersupport.org/",
    email: "info@connectionspeersupport.org",
    services: [
      "Peer Support",
      "Daily Recovery Activities",
      "Individual Support",
      "Warmline",
      "Transportation",
    ],
    description:
      "Peer-run center serving the Seacoast with groups, activities, and an evening warmline.",
    notes:
      "Warmline: (800) 809-6262 (5–10pm daily). Van rides available—call by 3pm prior day.",
  },
  {
    id: 139,
    name: "H.E.A.R.T.S. Peer Support Center (Greater Nashua)",
    category: "Primary SUD Providers",
    subcategory: "PSC",
    counties: ["All", "Hillsborough"],
    address: "5 Pine St Ext, Suite 1G, Nashua, NH",
    phone: "(603) 882-8400",
    website: "https://hearts-psa.org/",
    email: "kenl@hearts-psa.org",
    services: [
      "Peer Support",
      "Daily Recovery Activities",
      "Individual Support",
      "Warmline",
    ],
    description:
      "Member-run peer support center for Greater Nashua offering groups, activities, and warmline.",
    notes: "Warmline: (800) 306-4334 (evenings).",
  },
  {
    id: 140,
    name: "Infinity Peer Support",
    category: "Primary SUD Providers",
    subcategory: "PSC",
    counties: ["All", "Strafford"],
    address: "55 Summer St, Rochester, NH 03867",
    phone: "(603) 948-1036",
    website: "https://infinitypeersupport.org/",
    email: "info@infinitypeersupport.org",
    services: [
      "Peer Support",
      "Daily Recovery Activities",
      "Individual Support",
      "Warmline",
    ],
    description:
      "Strafford County’s peer-run center (formerly Tri-City Co-Op) with groups and evening warmline.",
    notes: "Warmline: (800) 809-6262 (5–10pm daily).",
  },
  {
    id: 141,
    name: "Littleton Peer Support (North Country PSA – Littleton)",
    category: "Primary SUD Providers",
    subcategory: "PSC",
    counties: ["All", "Grafton"],
    address:
      "Tannery Marketplace, 111 Saranac St, Suite 169, Littleton, NH 03561",
    phone: "(603) 575-5419",
    website: "https://www.northcountrypsa.org/",
    email: "alcexecdirector@gmail.com",
    services: [
      "Peer Support",
      "Daily Recovery Activities",
      "Individual Support",
      "Warmline",
    ],
    description:
      "North Country Peer Support’s Littleton site; peer-run groups and evening warmline.",
    notes: "Warmline: (866) 447-1765 (evenings).",
  },
  {
    id: 142,
    name: "Monadnock Peer Support Agency",
    category: "Primary SUD Providers",
    subcategory: "PSC",
    counties: ["All", "Cheshire"],
    address: "24 Vernon St, Keene, NH 03431",
    phone: "(603) 352-5093",
    website: "https://www.monadnockpsa.org/",
    email: "karen@monadnockpsa.org",
    services: [
      "Peer Support",
      "Daily Recovery Activities",
      "Individual Support",
      "Warmline",
    ],
    description:
      "Member-run peer support center for the Monadnock Region with groups and warmline.",
    notes: "Warmline: (603) 352-5094.",
  },
  {
    id: 143,
    name: "Next Step (Upper Valley)",
    category: "Primary SUD Providers",
    subcategory: "PSC",
    counties: ["All", "Grafton"],
    address: "109 Bank St, Lebanon, NH 03766",
    phone: "(603) 448-6941",
    website: "https://www.steppingstonenextstep.org/",
    email: "info@steppingstonenextstep.org",
    services: [
      "Peer Support",
      "Daily Recovery Activities",
      "Individual Support",
    ],
    description:
      "Upper Valley peer-run center (sister site to Stepping Stone in Claremont).",
    notes: "Call for current hours and group schedule.",
  },
  {
    id: 144,
    name: "One Peer (Concord Site)",
    category: "Primary SUD Providers",
    subcategory: "PSC",
    counties: ["All", "Merrimack"],
    address: "22 Bridge St, Suite 15, Concord, NH 03301",
    phone: "(603) 224-0894",
    website: "https://onepeer.org/",
    email: "michelle@onepeer.org",
    services: [
      "Peer Support",
      "Daily Recovery Activities",
      "Individual Support",
      "Warmline",
    ],
    description:
      "Lakes Region Consumer Advisory Board’s Concord peer support site (rebranded as One Peer).",
    notes:
      "Also operates CornerBridge in Laconia. Check site for warmline details.",
  },
  {
    id: 147,
    name: "Serenity Steps (North Country PSA – Berlin)",
    category: "Primary SUD Providers",
    subcategory: "PSC",
    counties: ["All", "Coös"],
    address: "567 Main St, Berlin, NH 03570",
    phone: "(603) 215-6404",
    website: "https://www.northcountrypsa.org/",
    email: "serenitystepspsc@gmail.com",
    services: [
      "Peer Support",
      "Daily Recovery Activities",
      "Individual Support",
      "Warmline",
    ],
    description:
      "North Country Peer Support’s Berlin site providing peer support and warmline.",
    notes: "Warmline: (866) 447-1765 (evenings).",
  },
  {
    id: 148,
    name: "North Country Peer Support Center – Groveton",
    category: "Primary SUD Providers",
    subcategory: "PSC",
    counties: ["All", "Coös"],
    address: "9 Summer St, Unit 1, Groveton, NH 03582",
    phone: "(866) 447-1765",
    website: "https://www.northcountrypsa.org/",
    email: "alcexecdirector@gmail.com",
    services: [
      "Peer Support",
      "Daily Recovery Activities",
      "Individual Support",
      "Warmline",
    ],
    description:
      "North Country Peer Support’s Groveton site with peer-run support and warmline access.",
    notes:
      "Current state peer-support directory lists Groveton as the North Country location. Warmline operates during evening hours.",
  },

  // Primary Mental Health Providers
  {
    id: 26,
    name: "NAMI New Hampshire (NAMI NH)",
    category: "Primary Mental Health Providers",
    statewide: true,
    counties: ["All", "Merrimack"],
    address: "85 N State St, Concord, NH 03301",
    phone: "(603) 225-5359",
    website: "https://www.naminh.org",
    email: "info@naminh.org",
    services: [
      "Information & Resource Line",
      "Support Groups",
      "Education Programs (Family-to-Family, Peer-to-Peer)",
      "Suicide Prevention Training (Connect Program)",
      "Advocacy",
    ],
    description:
      "Statewide affiliate of NAMI providing education, support groups, information & resource line, and suicide-prevention training for individuals and families.",
    notes:
      "Info & Resource Line: 1-800-242-6264 (press 4). Not a crisis line—use 988 or NH Rapid Response (1-833-710-6477) for crises.",
  },
  {
    id: 151,
    name: "Seacoast Mental Health Center (SMHC)",
    category: "Primary Mental Health Providers",
    counties: ["All", "Rockingham"],
    address:
      "1145 Sagamore Ave, Portsmouth, NH 03801; 30 Magnolia Ln, Exeter, NH 03833",
    phone: "(603) 431-6703 (Portsmouth); (603) 772-2710 (Exeter)",
    website: "https://smhc-nh.org",
    email: "",
    services: [
      "Outpatient counseling (children, adults, older adults)",
      "Psychiatric medication management",
      "Case management",
      "Emergency/crisis services",
      "School-based services",
      "Integrated SUD treatment",
    ],
    description:
      "Community Mental Health Center serving the Seacoast. Provides therapy, psychiatry, crisis intervention, and integrated substance use services for all ages.",
    notes:
      "State-designated CMHC for Rockingham County; offers telehealth and sliding fee options.",
  },
  {
    id: 152,
    name: "Community Partners (Strafford County CMHC)",
    category: "Primary Mental Health Providers",
    counties: ["All", "Strafford"],
    address: "113 Crosby Rd, Suite 1, Dover, NH 03820",
    phone: "(603) 516-9300",
    website: "https://communitypartnersnh.org",
    email: "",
    services: [
      "Outpatient counseling",
      "Psychiatric services",
      "Case management",
      "Mobile crisis dispatch",
      "Developmental disability services",
      "Family supports",
    ],
    description:
      "Community Mental Health Center for Strafford County. Provides behavioral health care, developmental services, and emergency crisis response.",
    notes:
      "State-funded CMHC; after-hours crisis support through NH Rapid Response.",
  },
  {
    id: 153,
    name: "Center for Life Management (CLM)",
    category: "Primary Mental Health Providers",
    counties: ["All", "Rockingham"],
    address: "10 Tsienneto Rd, Derry, NH 03038; 103 Stiles Rd, Salem, NH 03079",
    phone: "(603) 434-1577",
    website: "https://centerforlifemanagement.org",
    email: "info@clmnh.org",
    services: [
      "Therapy (individual, family)",
      "Psychiatric care",
      "24/7 emergency services",
      "Case management",
      "Substance use counseling",
      "Partial Hospitalization / IOP",
    ],
    description:
      "Community Mental Health Center for southern Rockingham County. Offers outpatient therapy, psychiatry, crisis intervention, and SUD support.",
    notes:
      "State-designated provider; accepts Medicaid, Medicare, and private insurance.",
  },
  {
    id: 154,
    name: "Riverbend Community Mental Health",
    category: "Primary Mental Health Providers",
    counties: ["All", "Merrimack"],
    address: "10 West St, Concord, NH 03301",
    phone: "(603) 228-1600",
    website: "https://www.riverbendcmhc.org/",
    email: "",
    services: [
      "Outpatient therapy",
      "Psychiatric medication management",
      "Community support programs",
      "Emergency psychiatric services",
      "Substance use disorder treatment",
      "Residential programs",
    ],
    description:
      "Community Mental Health Center for Merrimack County. Provides mental health, addiction treatment, and crisis stabilization services.",
    notes:
      "Same-day walk-ins available; accepts Medicaid, Medicare, and private insurance.",
  },
  {
    id: 155,
    name: "New Hampshire Hospital",
    category: "Primary Mental Health Providers",
    statewide: true,
    counties: ["All", "Merrimack"],
    address: "36 Clinton St, Concord, NH 03301",
    phone: "(603) 271-5300",
    website:
      "https://www.dhhs.nh.gov/about-dhhs/locations-facilities/new-hampshire-hospital",
    email: "NHHAdminSupport@dhhs.nh.gov",
    services: [
      "Inpatient psychiatric treatment",
      "Adult psychiatric units",
      "Child & Adolescent unit",
      "Comprehensive evaluation",
      "Medication stabilization",
    ],
    description:
      "State-run psychiatric hospital providing acute inpatient care for youth and adults with severe mental health conditions.",
    notes:
      "Admissions generally through emergency departments or CMHC referral.",
  },
  {
    id: 156,
    name: "Hampstead Hospital & Residential Treatment Facility (Youth Behavioral Health)",
    category: "Primary Mental Health Providers",
    statewide: true,
    counties: ["All", "Rockingham"],
    address: "218 East Road, Hampstead, NH 03841",
    phone: "(603) 329-5311",
    website: "https://www.dartmouth-health.org/hampstead",
    email: "",
    services: [
      "Inpatient psychiatric care for youth",
      "Residential treatment facility (PRTF)",
      "Integrated SUD services for youth",
      "Family-inclusive therapy",
      "Aftercare and discharge planning",
    ],
    description:
      "State-owned specialty hospital for children and adolescents with serious mental health or co-occurring SUD needs.",
    notes:
      "Operated in partnership with Dartmouth Health; statewide referral resource.",
  },
  {
    id: 157,
    name: "Manchester VA Medical Center",
    category: "Primary Mental Health Providers",
    statewide: true,
    counties: ["All", "Hillsborough"],
    address: "718 Smyth Rd, Manchester, NH 03104",
    phone: "(603) 624-4366",
    website: "https://www.va.gov/manchester-health-care/",
    email: "",
    services: [
      "Outpatient mental health clinics",
      "PTSD and trauma programs",
      "Substance use disorder treatment",
      "Tele-mental health",
      "Veterans Crisis Line coordination",
    ],
    description:
      "Federal VA hospital providing comprehensive mental health and addiction treatment services to eligible veterans.",
    notes: "Eligibility limited to veterans enrolled in VA health care.",
  },
  {
    id: 158,
    name: "Manchester Vet Center (Hooksett)",
    category: "Primary Mental Health Providers",
    statewide: true,
    counties: ["All", "Merrimack"],
    address: "1461 Hooksett Rd, Suite B7, Hooksett, NH 03106",
    phone: "(603) 668-7060",
    website: "https://www.va.gov/manchester-vet-center/",
    email: "",
    services: [
      "Readjustment counseling",
      "Military sexual trauma counseling",
      "PTSD groups",
      "Bereavement counseling",
      "Substance use assessment and referral",
    ],
    description:
      "Community-based VA program providing confidential, no-cost counseling and support for veterans and their families.",
    notes:
      "Outstation also available in Portsmouth for Seacoast-area veterans.",
  },
  {
    id: 27,
    name: "Monadnock Family Services",
    category: "Primary Mental Health Providers",
    counties: ["All", "Cheshire", "Hillsborough"],
    address: "40 Avon Street, Keene, NH 03431",
    phone: "(603) 357-4400",
    website: "https://www.mfs.org/about-monadnock-family-services/office-locations/",
    email: "",
    services: [
      "Outpatient Counseling",
      "Psychiatric Services",
      "Case Management",
      "Community-Based Supports",
      "Mobile Crisis (via NH Rapid Response Access Point)",
    ],
    description:
      "Community mental health center for the Monadnock region with offices in Keene and Peterborough.",
    notes:
      "For 24/7 crisis, call/text NH Rapid Response at 1-833-710-6477 or dial 988.",
  },
  {
    id: 29,
    name: "Lakes Region Mental Health Center",
    category: "Primary Mental Health Providers",
    counties: ["All", "Belknap", "Grafton"],
    address: "40 Beacon St E, Laconia, NH 03246 (main)",
    phone: "(603) 524-1100",
    website: "https://lrmhc.org/",
    email: "",
    services: [
      "Outpatient Therapy (Adults/Children)",
      "Psychiatry/Medication Management",
      "Crisis & Emergency Services",
      "Community Support & Case Management",
      "Supported Employment & Housing Services",
    ],
    description:
      "Community mental health services for Belknap and southern Grafton counties; additional office in Plymouth.",
    notes:
      "Additional site: 81 Highland St, Plymouth, NH 03264. For crises use 988 or NH Rapid Response (1-833-710-6477).",
  },
  {
    id: 309,
    name: "Pathfinder Recovery New Hampshire",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: [
      "All",
      "Belknap",
      "Carroll",
      "Cheshire",
      "Coös",
      "Grafton",
      "Hillsborough",
      "Merrimack",
      "Rockingham",
      "Strafford",
      "Sullivan",
    ],
    address: "Virtual/Telehealth (statewide NH)",
    phone: "(603) 458-0598",
    website: "https://pathfinder-recovery.com/",
    email: "info@pathfinder-recovery.com",
    services: [
      "Virtual Detox (medically supervised at-home withdrawal management)",
      "Virtual Intensive Outpatient Program (IOP)",
      "Medication-Assisted Treatment (MAT)",
      "Individual Therapy",
      "Peer Recovery Support",
    ],
    description:
      "Pathfinder Recovery NH is a fully virtual detox and intensive outpatient program delivered in the client's home. It removes barriers to treatment by providing medical care, MAT, therapy, and peer support without requiring residential placement or travel. Serves adults throughout New Hampshire.",
    notes:
      "100% telehealth model. Accepts most major insurance. Designed to work around clients' existing obligations (work, family, school). Good option for rural NH residents with limited local access.",
  },
  {
    id: 311,
    name: "Mountain Valley Treatment Center",
    category: "Primary Mental Health Providers",
    counties: ["All", "Sullivan"],
    address: "703 River Rd., Plainfield, NH 03781",
    phone: "(603) 960-2400",
    website: "https://mountainvalleytreatment.org/",
    email: "admissions@mountainvalleytreatment.org",
    services: [
      "Short-Term Residential Treatment (Adolescents & Young Adults, ages 13-20)",
      "Exposure and Response Prevention (ERP) for OCD",
      "Cognitive Behavioral Therapy (CBT)",
      "Acceptance and Commitment Therapy (ACT)",
      "Dialectical Behavior Therapy (DBT)",
      "Family Therapy and Coaching",
      "Academic Continuity Program",
      "Mindfulness-Based Stress Reduction",
    ],
    description:
      "Mountain Valley Treatment Center is a residential program for adolescents and young adults (13-20) whose lives have been significantly limited by anxiety disorders and OCD. One of only a few non-hospital programs nationally specializing in intensive ERP for severe OCD. The immersive program integrates clinical care with daily structure, academics, and experiential learning in the Upper Valley of NH.",
    notes:
      "Located 20 minutes from Hanover/Dartmouth. Not a substance use treatment center; specializes in anxiety, OCD, co-occurring depression, ADHD. Accepts most major insurance. Contact admissions for current availability and insurance verification.",
  },
  {
    id: 310,
    name: "Ridge RTC (Adolescent Residential Treatment)",
    category: "Primary Mental Health Providers",
    counties: ["All", "Rockingham"],
    address: "New Hampshire campus (Windham area)",
    phone: "(866) 521-3997",
    website: "https://theridgertc.com/",
    email: "",
    services: [
      "Short-Term Adolescent Residential Treatment (ages 12-18)",
      "Intensive Individual Therapy (3+ sessions/week)",
      "Group Therapy (3 sessions/day)",
      "Family Therapy",
      "Psychiatric Care and Medication Management",
      "Dual Diagnosis (Mental Health + Substance Use)",
      "Academic Support",
    ],
    description:
      "Ridge RTC provides intensive, short-term residential treatment for teens ages 12-18 with mental health and co-occurring substance use issues. The program addresses root causes through CBT, DBT, trauma-focused therapy, and experiential modalities. One of the most clinically intensive adolescent programs in New England.",
    notes:
      "In-network with Aetna, Carelon, Anthem, Beacon, Magellan, Multiplan, Optum. Does NOT accept Medicaid/Medicare. NH and Maine campuses. Contact admissions 24/7 for assessment.",
  },
  {
    id: 308,
    name: "Seacoast Youth Services",
    category: ["Primary SUD Providers", "Primary Mental Health Providers"],
    subcategory: "Treatment Centers",
    counties: ["All", "Rockingham"],
    address: "100 High St, Suite 205, Exeter, NH 03833",
    phone: "(603) 422-8208",
    website: "https://www.seacoastyouthservices.org/",
    email: "",
    services: [
      "Adolescent Intensive Outpatient Program (IOP) - Odyssey Program",
      "Individual and Family Therapy",
      "Mental Health Counseling for Youth",
      "Alternative to Suspension Program",
      "Court Diversion Services",
      "Substance Use Prevention and Intervention",
      "Home-Based Therapeutic Services",
    ],
    description:
      "Seacoast Youth Services is a nonprofit providing prevention, intervention, and treatment for youth (ages 14-18) struggling with substance misuse and mental health challenges. The Odyssey IOP is a structured 10-week afterschool program meeting 3x/week. SYS also provides home-based family therapy, school-based programs, and court diversion.",
    notes:
      "Recently relocated from Seabrook to Exeter. Serves the Seacoast region. Licensed therapists trained in DBT, evidence-based practices. Accepts private insurance; contact for Medicaid/sliding scale options.",
  },
  {
    id: 306,
    name: "Southern NH Health Center for Recovery Management at Nashua",
    category: ["Primary SUD Providers", "Primary Mental Health Providers"],
    subcategory: "Treatment Centers",
    counties: ["All", "Hillsborough"],
    address: "17 Prospect St, Nashua, NH 03060",
    phone: "(603) 577-2208",
    website:
      "https://www.snhhealth.org/locations/center-for-recovery-management-at-nashua",
    email: "",
    services: [
      "Outpatient SUD Treatment",
      "Medication-Assisted Treatment (MAT)",
      "Individual and Group Counseling",
      "Care Coordination (Embedded Care Coordinator RN)",
      "Certified Recovery Support Worker",
      "Psychiatric Evaluation",
    ],
    description:
      "Southern NH Health's Center for Recovery Management is a hospital-affiliated outpatient clinic providing comprehensive SUD treatment for patients of all ages, including pregnant individuals. The multidisciplinary team includes addiction medicine providers, embedded care coordinators, mental health therapists, and certified recovery support workers.",
    notes:
      "Part of SolutionHealth system. Holistic, evidence-based approach. Accepts most insurance including Medicaid. Team includes psychiatrists, nurse practitioners, RNs, therapists, and peer support.",
  },
  {
    id: 30,
    name: "Coös County Family Health Services",
    category: "Primary Mental Health Providers",
    counties: ["All", "Coös"],
    address: "133 Pleasant St, Berlin, NH 03570",
    phone: "(603) 752-2040",
    website: "https://coosfamilyhealth.org/",
    email: "",
    services: [
      "Behavioral Health Counseling",
      "Primary Care Integration",
      "Care Coordination",
    ],
    description:
      "FQHC providing integrated primary and behavioral health care to Coös County residents.",
    notes:
      "Additional clinic: 2 Broadway Ave, Gorham, NH 03581 (603-466-2741).",
  },
  {
    id: 31,
    name: "West Central Behavioral Health",
    category: "Primary Mental Health Providers",
    counties: ["All", "Grafton", "Sullivan"],
    address: "85 Mechanic St, Suite 3, Lebanon, NH 03766",
    phone: "(603) 448-5610",
    website: "https://wcbh.org",
    email: "",
    services: [
      "Outpatient Counseling",
      "Psychiatric Services",
      "Mobile Crisis",
      "Community Support Services",
      "Child, Family & Adult Programs",
    ],
    description:
      "Community mental health center serving the Upper Valley and Sullivan County.",
    notes:
      "Additional offices: Claremont (52 W Pleasant St) and Newport. New client line often listed as (603) 542-5128.",
  },
  {
    id: 321,
    name: "Discover Health Group",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["All", "Hillsborough"],
    address: "74 Northeastern Blvd, Suite 21 A-B, Nashua, NH 03062",
    phone: "(603) 487-8555",
    website: "https://discoverhealthgroup.com/",
    email: "",
    services: [
      "Partial Hospitalization Program (PHP)",
      "Intensive Outpatient Program (IOP)",
      "Outpatient Treatment",
      "Dual Diagnosis (Co-occurring) Treatment",
      "Medication-Assisted Treatment (MAT)",
      "Telehealth Services",
      "Holistic Therapies (Yoga, Art, Music, Meditation)",
    ],
    description:
      "Discover Health Group is an outpatient SUD and dual-diagnosis treatment facility in Nashua offering PHP, IOP, and outpatient levels of care with same-day admissions. Treatment integrates evidence-based therapies with holistic approaches and supports multiple recovery pathways including 12-Step, SMART Recovery, and Dharma Recovery.",
    notes:
      "Flexible scheduling with morning, afternoon, and evening sessions. Telehealth available. Accepts most major insurance. Located in the Northbridge Business Center, accessible from Route 3.",
  },
  {
    id: 33,
    name: "Greater Nashua Mental Health",
    category: "Primary Mental Health Providers",
    counties: ["All", "Hillsborough"],
    address: "7 Prospect St, Nashua, NH 03060",
    phone: "(603) 889-6147",
    website: "https://gnmhc.org",
    email: "",
    services: [
      "Outpatient Counseling",
      "Psychiatry/Medication Management",
      "Open Access/Intake",
      "Mobile Crisis (via NH Rapid Response)",
      "Substance Use & Co-occurring Services",
    ],
    description:
      "Community mental health center for Nashua and surrounding communities.",
    notes: "For crises use 988 or NH Rapid Response (1-833-710-6477).",
  },
  {
    id: 34,
    name: "The Mental Health Center of Greater Manchester (MHCGM)",
    category: "Primary Mental Health Providers",
    counties: ["All", "Hillsborough"],
    address: "401 Cypress St, Manchester, NH 03103",
    phone: "(603) 668-4111",
    website: "https://www.mhcgm.org",
    email: "",
    services: [
      "Outpatient Counseling",
      "Psychiatry/Medication Management",
      "Mobile Crisis",
      "CCBHC Services",
      "Specialty & Community Programs",
    ],
    description:
      "Large community mental health center serving Greater Manchester.",
    notes: "24/7 crisis support via 988 or NH Rapid Response (1-833-710-6477).",
  },
  {
    id: 35,
    name: "Canterbury Behavioral Health (formerly New Freedom Academy)",
    category: "Primary Mental Health Providers",
    counties: ["All", "Merrimack"],
    address: "367 Shaker Rd, Canterbury, NH 03224",
    phone: "(855) 712-7784",
    website:
      "https://www.graniterecoverycenters.com/treatment-centers/canterbury-behavioral-health/",
    email: "",
    services: [
      "Inpatient Mental Health Treatment",
      "Residential/Step-Down (PHP/IOP listed via network)",
      "Co-occurring Disorders",
    ],
    description: "Granite Recovery Centers' 20-bed residential mental health facility for adults aged 18 and older, including people with co-occurring mental health and substance use disorders.",
    notes: "Formerly New Freedom Academy in Canterbury; renamed in February 2025. This is distinct from the current New Freedom Academy outpatient program in Salem. Contact admissions for eligibility and level-of-care assessment.",
  },
  {
    id: 36,
    name: "Averte (Hanover House & VT Campus)",
    category: "Primary Mental Health Providers",
    counties: ["All", "Grafton"],
    address:
      "Hanover, NH (Hanover House) & 996 Lower Plain, Bradford, VT 05033",
    phone: "(802) 222-4544",
    website: "https://averte.triviumlifeservices.org/",
    email: "",
    services: [
      "Residential Psychiatric Treatment",
      "Medication Management",
      "Psychosocial Rehabilitation",
      "Transitional Living",
    ],
    description:
      "Private residential psychiatric treatment with a New Hampshire location (Hanover House) and main campus in Bradford, VT; serves adults with serious mental illness.",
    notes: "Call for program eligibility and current bed availability.",
  },
  {
    id: 37,
    name: "Cambridge Eating Disorder Center – New Hampshire",
    category: "Primary Mental Health Providers",
    counties: ["All", "Merrimack"],
    address: "105 Loudon Rd, Building #2, Concord, NH 03301",
    phone: "(603) 715-5150",
    website: "https://www.eatingdisordercenter.org/concord-nh/",
    email: "info@cedcmail.com",
    services: [
      "Partial Hospitalization Program (PHP)",
      "Intensive Outpatient Program (IOP)",
      "Eating Disorder Specialty Care",
      "Family Support & Education",
    ],
    description:
      "Specialty eating-disorder PHP/IOP for adolescents and adults; residential care available at the MA campus.",
    notes: "Hybrid in-person/virtual PHP; virtual IOP available.",
  },
  {
    id: 38,
    name: "Endurance Behavioral Health (Adolescent PHP/IOP)",
    category: "Primary Mental Health Providers",
    counties: ["All", "Rockingham"],
    address: "823 Lafayette Rd, Seabrook, NH 03874",
    phone: "(603) 760-1942",
    website: "https://endurancebehavioralhealth.com",
    email: "",
    services: [
      "Adolescent Partial Hospitalization (PHP)",
      "Adolescent Intensive Outpatient (IOP)",
      "Family Involvement & Care Coordination",
    ],
    description:
      "Mental health PHP/IOP for adolescents and emerging adults (13–20), in-person and virtual.",
    notes:
      "Program treats a range of conditions including depression, anxiety, OCD, BPD traits, trauma-related symptoms, and school avoidance.",
  },
  {
    id: 39,
    name: "Frisbie Memorial Hospital – Behavioral Health",
    category: "Primary Mental Health Providers",
    counties: ["All", "Strafford"],
    address: "11 Whitehall Rd, Rochester, NH 03867",
    phone: "(603) 602-5872",
    website: "https://www.frisbiehospital.com/specialties/mental-health-and-wellness",
    email: "",
    services: [
      "Outpatient Behavioral Health",
      "Partial Hospitalization (PHP)",
      "Inpatient Senior Mental Health Care",
    ],
    description: "Frisbie provides outpatient behavioral health care and partial hospitalization in Rochester, plus short-term inpatient psychiatric care for adults aged 55 and older.",
    notes: "Call the listed Rochester PHP number to arrange an intake; no referral is required. Consult-A-Nurse: (844) 706-8773. Senior inpatient admissions: (603) 602-6171.",
  },
  {
    id: 40,
    name: "Parkland Medical Center – Behavioral Health",
    category: "Primary Mental Health Providers",
    counties: ["All", "Rockingham"],
    address: "1 Parkland Dr, Derry, NH 03038",
    phone: "(603) 432-1500",
    website: "https://www.parklandmedicalcenter.com/specialties/mental-health-and-wellness",
    email: "",
    services: [
      "Adult Behavioral Health",
      "Partial Hospitalization (PHP)",
      "Assessment & Referral",
    ],
    description:
      "Hospital-based behavioral health services in Derry with access to network PHP.",
    notes:
      "PHP scheduling line for Derry: (603) 421-3771; Concord PHP: (603) 421-2450.",
  },
  {
    id: 41,
    name: "WestBridge",
    category: "Primary Mental Health Providers",
    counties: ["All", "Hillsborough"],
    address: "660 Chestnut St, Manchester, NH 03104",
    phone: "(877) 461-7711",
    website: "https://www.westbridge.org",
    email: "inquiry@westbridge.org",
    services: [
      "Integrated Dual Diagnosis Treatment (Co-occurring SUD & Mental Illness)",
      "Residential & Community-Based Programs",
      "Family Education & Support",
      "Care Management",
    ],
    description:
      "Evidence-based integrated treatment for men with co-occurring mental illness and substance use disorders.",
    notes:
      "Call admissions for availability; outpatient and residential stabilization programs available.",
  },

  // Therapists
  {
    id: 42,
    name: "NH Therapists via Psychology Today",
    category: "Therapists",
    counties: [
      "All",
      "Belknap",
      "Carroll",
      "Cheshire",
      "Coös",
      "Grafton",
      "Hillsborough",
      "Merrimack",
      "Rockingham",
      "Strafford",
      "Sullivan",
    ],
    address: "",
    phone: "",
    website: "https://www.psychologytoday.com/us/therapists/nh",
    email: "",
    services: [
      "Therapy Directory",
      "Filter by Specialties, Insurance, Telehealth",
    ],
    description:
      "A statewide directory on Psychology Today listing licensed therapists in NH—you can filter by location, specialty, insurance, and telehealth availability.",
    notes:
      "Referral directory only; not a single provider. Use to find therapists near you.",
  },
  {
    id: 43,
    name: "Capital Valley Counseling Associates, LLC",
    category: "Therapists",
    counties: ["All", "Merrimack"],
    address: "8 Centre St, Suite 2, Concord, NH 03301",
    phone: "(603) 228-7300",
    website: "https://www.cvcanh.com/contact-us",
    email: "",
    services: [
      "Individual Therapy",
      "Couples & Family Therapy",
      "EAP",
      "Virtual Counseling",
      "EMDR",
    ],
    description:
      "Concord-based counseling practice offering in-person and teletherapy for individuals, couples, families, and EAP, including EMDR and career/family therapy.",
    notes:
      "Session rates around $170; accepts multiple insurances (Aetna, Anthem, Cigna, Tufts, Medicare, etc.)",
  },
  {
    id: 44,
    name: "LifeStance Health (NH Locations)",
    category: "Therapists",
    statewide: true,
    counties: ["All", "Hillsborough", "Merrimack", "Rockingham", "Strafford"],
    address: "Multiple NH locations (see notes)",
    phone: "",
    website: "https://lifestance.com/location/state/new-hampshire/",
    email: "",
    services: [
      "Therapy",
      "Psychiatry",
      "TMS",
      "Ketamine Therapy",
      "In-person & Telehealth",
    ],
    description:
      "Nationwide behavioral health provider with multiple NH clinics offering therapy, psychiatry, TMS, ketamine, and virtual visits.",
    notes:
      "NH sites include Nashua (15 Trafalgar Sq), Concord (70 Commercial St), Manchester (15 Nelson St), Portsmouth (539 Islington St), Exeter (24 Front St), Dover (16 Fifth St), Bedford (8 Commerce Dr).",
  },
  {
    id: 45,
    name: "Mending Minds Counseling PLLC",
    category: "Therapists",
    counties: ["All", "Rockingham"],
    address: "1 Middle St, Suite 235, Portsmouth, NH 03801–4484",
    phone: "(855) 604-8905",
    website: "https://mendingmindsllc.com",
    email: "",
    services: [
      "Individual, Couples & Family Therapy",
      "Group Therapy",
      "In-person & Online",
      "Specialty: Addiction, OCD, LGBTQ+, Youth, Families",
    ],
    description:
      "Downtown Portsmouth group therapy practice focusing on LGBTQ+, youth/adult, OCD, addiction with in-person and teletherapy by LCMHCs and MLADCs.",
    notes:
      "Offers free 15-minute consultation; self-pay and insurance accepted; multiple providers.",
  },

  // Ride Services
  {
    id: 47,
    name: "NH DHHS Fee-for-Service (CTS)",
    category: "Ride Services",
    counties: [
      "All",
      "Belknap",
      "Carroll",
      "Cheshire",
      "Coös",
      "Grafton",
      "Hillsborough",
      "Merrimack",
      "Rockingham",
      "Strafford",
      "Sullivan",
    ],
    address: "",
    phone: "844-259-4780",
    website:
      "https://www.dhhs.nh.gov/programs-services/medicaid/medicaid-transportation",
    email: "",
    services: [
      "Non-Emergency Medical Transportation (NEMT)",
      "Family & Friends Mileage Reimbursement",
      "Public Transit Passes (when applicable)",
      "Emergency or Urgent NEMT",
    ],
    description:
      "DHHS’s Fee-for-Service Medicaid non-emergency transport program managed by CTS; covers rides, mileage, and public transit options for eligible riders.",
    notes:
      "Routine NEMT requires 48-hour notice; urgent/rush rides require 2-hour notice. See DHHS website for schedule and eligibility rules.",
  },
  {
    id: 48,
    name: "NH Healthy Families (MTM - Medicaid)",
    category: "Ride Services",
    counties: [
      "All",
      "Belknap",
      "Carroll",
      "Cheshire",
      "Coös",
      "Grafton",
      "Hillsborough",
      "Merrimack",
      "Rockingham",
      "Strafford",
      "Sullivan",
    ],
    address: "",
    phone: "888-597-1192",
    website: "https://www.nhhealthyfamilies.com/members/medicaid/benefits-services/benefits-overview.html",
    email: "",
    services: [
      "Non-Emergency Medical Transportation (NEMT) for NH Healthy Families (Centene)",
      "Family & Friends Mileage Reimbursement",
      "Urgent/Rush Ride Requests",
    ],
    description: "NH Healthy Families members access covered non-emergency medical transportation through MTM. Request routine rides at least 2 business days in advance.",
    notes: "Urgent non-emergency rides require 3 hours' notice. Call MTM for after-hours transportation or hospital discharge; family and friends mileage reimbursement requires advance approval.",
  },
  {
    id: 49,
    name: "WellSense Health Plan (CTS)",
    category: "Ride Services",
    counties: [
      "All",
      "Belknap",
      "Carroll",
      "Cheshire",
      "Coös",
      "Grafton",
      "Hillsborough",
      "Merrimack",
      "Rockingham",
      "Strafford",
      "Sullivan",
    ],
    address: "",
    phone: "844-909-7433 (TTY 711)",
    website:
      "https://www.wellsense.org/members/nh/new-hampshire-medicaid/transportation",
    email: "",
    services: [
      "Non-Emergency Medical Transportation (NEMT)",
      "Family & Friends Mileage Reimbursement",
      "Online Scheduling via MTM Portal",
      "Public Transit Options",
    ],
    description:
      "WellSense-managed NEMT services through CTS for Medicaid members; rides can be scheduled 24/7 via phone or online MTM portal, with 2-business-day notice recommended.",
    notes: "Use online portal to schedule/reschedule/cancel rides.",
  },
  {
    id: 50,
    name: "AmeriHealth Caritas New Hampshire (CTS)",
    category: "Ride Services",
    counties: [
      "All",
      "Belknap",
      "Carroll",
      "Cheshire",
      "Coös",
      "Grafton",
      "Hillsborough",
      "Merrimack",
      "Rockingham",
      "Strafford",
      "Sullivan",
    ],
    address: "",
    phone: "833-301-2264",
    website:
      "https://www.amerihealthcaritasnh.com/member/benefits/transportation",
    email: "",
    services: [
      "Non-Emergency Medical Transportation (NEMT)",
      "Family & Friends Mileage Reimbursement",
      "Urgent/Rush Ride Handling",
      "Routine and Emergency Discharge Trips",
    ],
    description:
      "AmeriHealth Caritas NH Medicaid NEMT provided via CTS; members must call in advance (routine: 48 hrs, urgent: 2 hrs) for rides to covered services.",
    notes:
      "Same-day requests allowed for urgent medical needs; reimbursement is $0.67/mile if using Family & Friends option.",
  },

  // Crisis Intervention Services
  {
    id: 324,
    name: "New Beginnings - Without Violence and Abuse",
    category: "Crisis Intervention Services",
    counties: ["All", "Belknap"],
    address: "PO Box 622, Laconia, NH 03247",
    phone: "(866) 841-6229 (24/7 crisis line); (603) 528-6511 (office)",
    website: "https://www.newbeginningsnh.org/",
    email: "",
    services: [
      "24/7 Crisis Hotline",
      "Emergency Shelter",
      "Court Advocacy and Protective Orders",
      "Support Groups",
      "Safety Planning",
      "Community Education",
    ],
    description:
      "New Beginnings is the designated domestic and sexual violence crisis center serving all of Belknap County. Provides free, confidential services including 24/7 crisis support, emergency shelter, court advocacy, and support groups.",
    notes:
      "Member of NHCADSV. Services available regardless of gender, age, orientation, or immigration status. Office in Laconia; shelter address confidential.",
  },

  {
    id: 325,
    name: "REACH Crisis Services at YWCA NH",
    category: "Crisis Intervention Services",
    counties: ["All", "Hillsborough"],
    address: "72 Concord St, Manchester, NH 03101",
    phone: "(603) 668-2299 (24/7 crisis line); (603) 625-5785 (office)",
    website: "https://ywcanh.org/reach/",
    email: "",
    services: [
      "24/7 Crisis Hotline",
      "Emergency Shelter",
      "Court Advocacy",
      "Support Groups",
      "Safety Planning",
    ],
    description:
      "REACH at YWCA NH is the designated domestic and sexual violence crisis center for the Greater Manchester area of Hillsborough County. Provides 24/7 crisis support, emergency shelter, advocacy, and education.",
    notes:
      "Member of NHCADSV. Serves Manchester and surrounding communities in eastern Hillsborough County. Bridges (id next) covers Nashua/western Hillsborough.",
  },

  {
    id: 326,
    name: "Bridges: Domestic and Sexual Violence Support",
    category: "Crisis Intervention Services",
    counties: ["All", "Hillsborough"],
    address: "PO Box 217, Nashua, NH 03061",
    phone: "(603) 883-3044 (24/7 crisis line); (603) 889-0858 (Nashua office)",
    website: "https://www.bridgesnh.org/",
    email: "",
    services: [
      "24/7 Crisis Hotline",
      "Emergency Shelter",
      "Court Advocacy and Protective Orders",
      "Support Groups",
      "Outreach and Education",
    ],
    description:
      "Bridges is the designated domestic and sexual violence crisis center for the Greater Nashua and Souhegan Valley areas of Hillsborough County. Provides free, confidential 24/7 crisis intervention, shelter, advocacy, and prevention education.",
    notes:
      "Member of NHCADSV. Nashua office: (603) 889-0858. Milford office: (603) 672-9833. Accepts collect calls. Interpreters available in 140+ languages.",
    contacts: [
      { type: "24/7 Crisis Line", phone: "(603) 883-3044" },
      { type: "Nashua Office", phone: "(603) 889-0858" },
      { type: "Milford Office", phone: "(603) 672-9833" },
    ],
  },

  {
    id: 327,
    name: "WISE (Domestic & Sexual Violence Services)",
    category: "Crisis Intervention Services",
    counties: ["All", "Grafton", "Sullivan"],
    address: "38 Bank St, Lebanon, NH 03766",
    phone: "(603) 448-5525 (24/7 crisis line)",
    website: "https://wiseuv.org/",
    email: "",
    services: [
      "24/7 Crisis Hotline",
      "Emergency Shelter",
      "Court Advocacy",
      "Support Groups",
      "Prevention Education",
    ],
    description:
      "WISE is the designated domestic and sexual violence crisis center for the Upper Valley, serving Grafton and eastern Sullivan counties. Provides free, confidential 24/7 crisis support, shelter, court advocacy, and community education.",
    notes:
      "Member of NHCADSV. Formerly 'Women's Information Service'. Office in Lebanon; shelter address confidential.",
  },

  {
    id: 328,
    name: "Starting Point: Services for Victims of Domestic & Sexual Violence",
    category: "Crisis Intervention Services",
    counties: ["All", "Carroll"],
    address: "PO Box 1972, Conway, NH 03818",
    phone: "(800) 336-3795 (24/7 crisis line); (603) 447-2494 (Conway office)",
    website: "https://www.startingpointnh.org/",
    email: "",
    services: [
      "24/7 Crisis Hotline",
      "Emergency Shelter",
      "Court Advocacy",
      "Support Groups",
      "Safety Planning",
    ],
    description:
      "Starting Point is the designated domestic and sexual violence crisis center serving all of Carroll County. Provides 24/7 crisis support, shelter, court advocacy, and community education.",
    notes:
      "Member of NHCADSV. Conway office: (603) 447-2494. Wolfeboro office: (603) 452-8014. Shelter address confidential.",
    contacts: [
      { type: "24/7 Crisis Line", phone: "(800) 336-3795" },
      { type: "Conway Office", phone: "(603) 447-2494" },
      { type: "Wolfeboro Office", phone: "(603) 452-8014" },
    ],
  },

  {
    id: 329,
    name: "Thrive Survivor Support Center (formerly CCCNH)",
    category: "Crisis Intervention Services",
    counties: ["All", "Merrimack"],
    address: "PO Box 1344, Concord, NH 03302",
    phone: "(866) 841-6229",
    website: "https://thrivenh.org/services/",
    email: "",
    services: [
      "24/7 Crisis Hotline",
      "Emergency Shelter",
      "Court Advocacy",
      "Support Groups",
      "Safety Planning",
    ],
    description:
      "Thrive Survivor Support Center, formerly the Crisis Center of Central NH, is the designated domestic and sexual violence crisis center for Merrimack County. Provides free, confidential 24/7 crisis support, emergency shelter, court advocacy, and education.",
    notes:
      "Office: (603) 225-7376. Member of NHCADSV. Serves all of Merrimack County including Concord, Franklin, and surrounding communities.",
  },

  {
    id: 330,
    name: "RESPONSE (Seacoast DV/SA Crisis Center)",
    category: "Crisis Intervention Services",
    counties: ["All", "Rockingham"],
    address: "PO Box 1344, Portsmouth, NH 03802",
    phone: "(888) 747-7070 (24/7 crisis line)",
    website: "https://responsenh.org/",
    email: "",
    services: [
      "24/7 Crisis Hotline",
      "Emergency Shelter",
      "Court Advocacy",
      "Support Groups",
      "Prevention Education",
    ],
    description:
      "RESPONSE is the designated domestic and sexual violence crisis center serving Rockingham County and the Seacoast. Provides 24/7 crisis intervention, shelter, court advocacy, and community education.",
    notes:
      "Member of NHCADSV. Serves the entire Seacoast/Rockingham County area.",
  },

  {
    id: 331,
    name: "HAVEN (formerly A Safe Place and SASS)",
    category: "Crisis Intervention Services",
    counties: ["Rockingham","Strafford"],
    address: "20 International Dr, Suite 300, Portsmouth, NH 03801; 150 Wakefield St, Suite 16, Rochester, NH 03867",
    phone: "(603) 994-7233",
    website: "https://havennh.org/about-us/our-mission",
    email: "info@havennh.org",
    services: [
      "24/7 Crisis Hotline",
      "Emergency Shelter",
      "Court Advocacy",
      "Support Groups",
      "Community Education",
    ],
    description: "Domestic and sexual violence support and prevention agency serving Rockingham and Strafford counties. A Safe Place and SASS merged to become HAVEN in 2015.",
    notes: "Free confidential support is available 24/7 at the listed hotline. In-person advocacy is by appointment; call first. Business office: (603) 436-4107. Online advocate chat is available Monday–Friday, 9am–4pm, excluding holidays.",
  },

  {
    id: 332,
    name: "The Center for Safer Communities – Keene (formerly MCVP)",
    category: "Crisis Intervention Services",
    counties: ["All", "Cheshire", "Hillsborough"],
    address: "12 Court Street, Suite 103, Keene, NH 03431",
    phone: "(888) 511-6287 (24/7 crisis line); (603) 352-3782 (office)",
    website: "https://cscnh.org/",
    email: "",
    services: [
      "24/7 Crisis Hotline",
      "Emergency Shelter",
      "Court Advocacy",
      "Support Groups",
      "Prevention Education",
    ],
    description:
      "The Center for Safer Communities is the current home of former MCVP programs, providing free and confidential crisis, shelter and housing, advocacy, peer-support, and prevention services in Cheshire and western Hillsborough counties.",
    notes:
      "Transition effective October 1, 2026; the Keene office retains local advocates and the existing MCVP helplines.",
  },

  {
    id: 333,
    name: "Voices Against Violence",
    category: "Crisis Intervention Services",
    counties: ["All", "Grafton"],
    address: "PO Box 53, Plymouth, NH 03264",
    phone: "(877) 221-6176 (24/7 crisis line); (603) 536-5999 (office)",
    website: "https://www.voicesagainstviolence.net/",
    email: "",
    services: [
      "24/7 Crisis Hotline",
      "Emergency Shelter",
      "Court Advocacy",
      "Support Groups",
    ],
    description:
      "Voices Against Violence is the designated domestic and sexual violence crisis center for central Grafton County (Plymouth/Lakes Region). Provides 24/7 crisis support, shelter, advocacy, and education.",
    notes:
      "Member of NHCADSV. Serves the Plymouth/central Grafton area. WISE (id 327) covers the Upper Valley/southern Grafton.",
    contacts: [
      { type: "24/7 Crisis Line", phone: "(877) 221-6176" },
      { type: "Local Crisis Line", phone: "(603) 536-1659" },
      { type: "Office", phone: "(603) 536-5999" },
    ],
  },

  {
    id: 52,
    name: "NH Rapid Response Access Point (NHRRAP)",
    category: "Crisis Intervention Services",
    counties: [
      "All",
      "Belknap",
      "Carroll",
      "Cheshire",
      "Coös",
      "Grafton",
      "Hillsborough",
      "Merrimack",
      "Rockingham",
      "Strafford",
      "Sullivan",
    ],
    address: "",
    phone: "Call/Text: 833-710-6477 (24/7) or dial 988",
    website: "https://nh988.com",
    email: "",
    services: [
      "24/7 telephonic, text, and chat crisis support",
      "Crisis de-escalation, safety planning, and referrals",
      "Dispatch of Mobile Crisis Response Teams (MCRT)",
      "Same-day / next-day CMHC access appointments",
      "Free and confidential for NH residents",
    ],
    description:
      "NH’s statewide behavioral health crisis system—call, text, or chat to reach trained crisis operators who can talk you through a mental health or SUD crisis, activate in-person Mobile Crisis Response Teams, or connect you to Community Mental Health Center follow-up.",
    notes:
      "Operated by Protocall on behalf of NH DHHS; mobile teams respond within ~1 hour and are dispatched statewide by your local CMHC. Services are free at the point of use; billing is handled through CMHCs.",
  },
  {
    id: 312,
    name: "NH Coalition Against Domestic and Sexual Violence (NHCADSV)",
    category: "Crisis Intervention Services",
    counties: [
      "All",
      "Belknap",
      "Carroll",
      "Cheshire",
      "Coös",
      "Grafton",
      "Hillsborough",
      "Merrimack",
      "Rockingham",
      "Strafford",
      "Sullivan",
    ],
    address: "Statewide (12 member crisis centers)",
    phone: "(866) 644-3574",
    website: "https://www.nhcadsv.org/",
    email: "",
    services: ["24/7 Statewide Domestic and Sexual Violence Helpline","Emergency Shelter (via member crisis centers)","Court Advocacy and Protective Order Assistance","Support Groups","Safety Planning","Community Education and Outreach"],
    description:
      "NHCADSV is the statewide umbrella organization for 12 member crisis centers providing free, confidential services to victims and survivors of domestic violence, sexual assault, stalking, and human trafficking. Services are open and affirming to all regardless of gender, age, orientation, or immigration status.",
    notes: "Call the statewide helpline to reach a confidential advocate in your community. Use NHCADSV's current member-program directory for local crisis centers and catchment areas. You do not need to be in crisis to call.",
    contacts: [
      { type: "Domestic and Sexual Violence Helpline (24/7)", phone: "(866) 644-3574" },
    ],
  },
  // Online Resources and Support Groups
  // (all with counties expanded)
  {
    id: 53,
    name: "Al-Anon/Alateen Family Groups of New Hampshire",
    category: "Online Resources and Support Groups",
    statewide: true,
    counties: [
      "All",
      "Belknap",
      "Carroll",
      "Cheshire",
      "Coös",
      "Grafton",
      "Hillsborough",
      "Merrimack",
      "Rockingham",
      "Strafford",
      "Sullivan",
    ],
    address: "",
    phone: "(603) 369-6930",
    website: "https://nhal-anon.org/find-a-meeting/",
    email: "outreach@nhal-anon.org",
    services: [
      "New Hampshire in-person Al-Anon meetings",
      "New Hampshire online and telephone meetings",
      "Alateen meetings and resources",
      "Meeting finder and printable NH meeting list",
    ],
    description:
      "New Hampshire-specific Al-Anon and Alateen pathway for families and friends affected by another person’s alcohol use.",
    notes:
      "Use the current NH meeting finder or contact the NH answering service before attending.",
  },
  {
    id: 54,
    name: "In The Rooms",
    category: "Online Resources and Support Groups",
    counties: [
      "All",
      "Belknap",
      "Carroll",
      "Cheshire",
      "Coös",
      "Grafton",
      "Hillsborough",
      "Merrimack",
      "Rockingham",
      "Strafford",
      "Sullivan",
    ],
    address: "",
    phone: "",
    website: "https://www.intherooms.com/livemeetings/list",
    email: "",
    services: [
      "Live online meetings (12-Step & alternative fellowships)",
      "Attendance verification",
      "24/7 recovery community",
    ],
    description:
      "Large online recovery community hosting daily live meetings across many pathways (AA, NA, SMART, and more).",
    notes:
      "Digital, signed attendance verifications available via profile per FAQ.",
  },
  {
    id: 55,
    name: "SMART Recovery New Hampshire",
    category: "Online Resources and Support Groups",
    statewide: true,
    counties: [
      "All",
      "Belknap",
      "Carroll",
      "Cheshire",
      "Coös",
      "Grafton",
      "Hillsborough",
      "Merrimack",
      "Rockingham",
      "Strafford",
      "Sullivan",
    ],
    address: "",
    phone: "",
    website: "https://www.smartrecoverynh.org/find-a-meeting",
    email: "",
    services: [
      "New Hampshire in-person SMART Recovery meetings",
      "Online SMART Recovery meetings",
      "Free facilitated 4-Point Recovery groups",
      "CBT- and REBT-based self-management tools",
    ],
    description:
      "New Hampshire meeting pathway for free SMART Recovery mutual-support groups addressing substance use and other addictive behaviors.",
    notes:
      "Meeting schedules and locations change. Confirm the current listing through the NH page or official SMARTfinder before attending.",
  },
  {
    id: 56,
    name: "NAMI Online Support Groups",
    category: "Online Resources and Support Groups",
    counties: [
      "All",
      "Belknap",
      "Carroll",
      "Cheshire",
      "Coös",
      "Grafton",
      "Hillsborough",
      "Merrimack",
      "Rockingham",
      "Strafford",
      "Sullivan",
    ],
    address: "",
    phone: "",
    website: "https://www.nami.org/support-groups/",
    email: "",
    services: [
      "NAMI Connection (for adults with MH conditions)",
      "NAMI Family Support Group (for family/caregivers)",
      "Peer-led, no-cost groups (many offered virtually)",
    ],
    description:
      "NAMI’s peer-led support groups with options that local/state affiliates host online; free and confidential.",
    notes:
      "Check your state/affiliate (e.g., NAMI NH) for virtual group schedules and registration.",
  },
  {
    id: 320,
    name: "She Recovers",
    category: "Online Resources and Support Groups",
    counties: [
      "All",
      "Belknap",
      "Carroll",
      "Cheshire",
      "Coös",
      "Grafton",
      "Hillsborough",
      "Merrimack",
      "Rockingham",
      "Strafford",
      "Sullivan",
    ],
    address: "",
    phone: "",
    website: "https://sherecovers.org/",
    email: "",
    services: [
      "Online and In-Person Recovery Community (Women & Non-Binary)",
      "Virtual Support Groups and Workshops",
      "Coaching and Resources",
      "Annual Conferences and Retreats",
    ],
    description:
      "She Recovers connects, supports, and empowers women and non-binary individuals in recovery from substance use disorders, mental health issues, trauma, and related life challenges through virtual and in-person community offerings.",
    notes:
      "Not a treatment provider. Peer community and support network. Free online resources; some events/coaching are fee-based.",
  },

  {
    id: 323,
    name: "Never Use Alone",
    category: "Online Resources and Support Groups",
    counties: [
      "All",
      "Belknap",
      "Carroll",
      "Cheshire",
      "Coös",
      "Grafton",
      "Hillsborough",
      "Merrimack",
      "Rockingham",
      "Strafford",
      "Sullivan",
    ],
    address: "",
    phone: "(800) 484-3731",
    website: "https://neverusealone.com/",
    email: "",
    services: [
      "Overdose Prevention Hotline (24/7)",
      "Phone-based Harm Reduction",
      "Volunteer Operators Stay on the Line During Use",
      "Emergency Services Dispatched if Overdose Detected",
    ],
    description:
      "Never Use Alone operates a free, nonjudgmental, 24/7 hotline for people who use drugs alone. Callers provide their name and location, and a trained operator stays on the line. If an overdose is detected, emergency services are dispatched to the caller's location. This service saves lives by eliminating the primary risk factor in fatal overdoses: using alone.",
    notes:
      "100% free and confidential. No questions asked. Not a treatment referral line. Purely harm reduction / overdose prevention. Available to anyone in the US.",
  },

  {
    id: 322,
    name: "Creating Connections NH (Alternative Peer Groups)",
    category: "Online Resources and Support Groups",
    counties: [
      "All",
      "Belknap",
      "Carroll",
      "Cheshire",
      "Coös",
      "Grafton",
      "Hillsborough",
      "Merrimack",
      "Rockingham",
      "Strafford",
      "Sullivan",
    ],
    address: "",
    phone: "",
    website: "https://iod.unh.edu/substance-use-youth/alternative-peer-groups",
    email: "",
    services: [
      "Alternative Peer Groups (APGs) for Youth and Young Adults",
      "Substance-Free Social Activities",
      "Recovery-Focused Community for Young People",
    ],
    description:
      "Creating Connections NH offers Alternative Peer Groups (APGs) providing youth and young adults with substance use challenges a supportive, substance-free environment to connect with recovery-focused peers through social activities and community building.",
    notes:
      "Statewide initiative. APGs are designed for young people who may not connect with traditional 12-step or adult recovery settings. Check website for active groups and meeting locations.",
  },
  {
    id: 57,
    name: "DBSA Online Support Groups",
    category: "Online Resources and Support Groups",
    counties: [
      "All",
      "Belknap",
      "Carroll",
      "Cheshire",
      "Coös",
      "Grafton",
      "Hillsborough",
      "Merrimack",
      "Rockingham",
      "Strafford",
      "Sullivan",
    ],
    address: "",
    phone: "",
    website: "https://www.dbsalliance.org/dbsa-online-support-groups/",
    email: "",
    services: [
      "Online peer support groups for depression & bipolar",
      "Topic-/identity-specific groups available",
      "Free registration",
    ],
    description:
      "DBSA hosts moderated, peer-led online groups offering a safe space to share experiences and coping skills.",
    notes:
      "Some groups hosted on HeyPeers—see DBSA’s site for registration and schedule.",
  },
  {
    id: 58,
    name: "ADAA Online Peer Support Community",
    category: "Online Resources and Support Groups",
    counties: [
      "All",
      "Belknap",
      "Carroll",
      "Cheshire",
      "Coös",
      "Grafton",
      "Hillsborough",
      "Merrimack",
      "Rockingham",
      "Strafford",
      "Sullivan",
    ],
    address: "",
    phone: "",
    website: "https://healthunlocked.com/anxiety-depression-support",
    email: "",
    services: [
      "Peer-to-peer discussion community",
      "Moderation by ADAA partners",
      "Free, anonymous accounts",
    ],
    description:
      "Anxiety and Depression Association of America’s online community (hosted on HealthUnlocked) for peer connection and shared learning.",
    notes: "Not therapy; community is moderated and education-focused.",
  },
  {
    id: 59,
    name: "Nar-Anon Family Groups (Virtual Meetings)",
    category: "Online Resources and Support Groups",
    counties: [
      "All",
      "Belknap",
      "Carroll",
      "Cheshire",
      "Coös",
      "Grafton",
      "Hillsborough",
      "Merrimack",
      "Rockingham",
      "Strafford",
      "Sullivan",
    ],
    address: "",
    phone: "",
    website: "https://nar-anon.org/meetings-page/",
    email: "",
    services: [
      "Online meetings for families/friends affected by someone’s drug use",
      "Multilingual meeting listings",
      "Email contact for meeting links",
    ],
    description: "Official Nar-Anon meeting directory for families and friends affected by someone else's addiction, with in-person and virtual meeting options.",
    notes: "Select virtual meetings and check the individual listing for access instructions and contact details.",
  },
  {
    id: 159,
    name: "New Hampshire Outright",
    category: "Online Resources and Support Groups",
    statewide: true,
    counties: [
      "All",
      "Belknap",
      "Carroll",
      "Cheshire",
      "Coös",
      "Grafton",
      "Hillsborough",
      "Merrimack",
      "Rockingham",
      "Strafford",
      "Sullivan",
    ],
    address: "PO Box 842, Portsmouth, NH 03802",
    phone: "(603) 552-5824",
    website: "https://www.nhoutright.org/",
    email: "mail@nhoutright.org",
    services: [
      "Support groups for LGBTQ+ youth",
      "Peer support and Discord server",
      "Educational workshops",
      "Free binder/trans gear program",
      "Parent support groups",
    ],
    description:
      "Nonprofit supporting LGBTQ+ youth across NH with peer groups, events, and identity-affirming resources.",
    notes:
      "Programs are free and confidential; based in Portsmouth with statewide reach via virtual services.",
  },
  {
    id: 161,
    name: "Heartfelt Recovery Centers",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["Hillsborough", "Belknap"],
    address: "Hudson & Laconia, NH (multiple locations)",
    phone: "(603) 207-1633",
    website: "https://heartfeltrecoverycenters.com/",
    email: "info@heartfeltrecoverycenter.com",
    services: [
      "Detox Placement",
      "Inpatient Treatment",
      "Partial Hospitalization (PHP)",
      "Intensive Outpatient (IOP)",
      "Outpatient Program (OP)",
      "Medication-Assisted Treatment (MAT)",
      "Dual Diagnosis Treatment",
      "Telehealth",
    ],
    description:
      "Comprehensive alcohol and drug treatment provider with locations in Hudson and Laconia, NH, offering detox placement, inpatient residential treatment, and a full continuum of outpatient services (PHP, IOP, OP), with integrated care for co-occurring mental health issues.",
    notes:
      "Hudson: 41 Sagamore Park Rd, Hudson, NH 03051. Laconia: 350 Court St, Laconia, NH 03246.",
    contacts: [{ type: "General Admissions", phone: "(603) 207-1633" }],
  },
  {
    id: 162,
    name: "Elliot Hospital – The Doorway of Greater Manchester",
    category: "Primary SUD Providers",
    subcategory: ["Treatment Centers"],
    counties: ["All", "Hillsborough"],
    address: "300 Hanover St, Manchester, NH 03103",
    phone: "(603) 663-4430",
    website:
      "https://www.elliothospital.org/services/browse-all-services/substance-use-disorder-services/greater-manchester-doorway-program",
    email: "",
    services: [
      "Substance use disorder screening and assessment (The Doorway)",
      "Care coordination and referral to detox, residential, and outpatient treatment",
      "Linkage to Medication-Assisted Treatment (MAT)",
      "Peer recovery support",
      "Emergency addiction response and referral",
    ],
    description:
      "Elliot Hospital operates The Doorway of Greater Manchester, providing centralized SUD intake, screening, and referral to detox, residential, outpatient, and recovery-support services.",
    notes:
      "Current Greater Manchester Doorway location. Statewide Doorway access is also available through 2-1-1.",
    contacts: [
      { type: "Elliot Hospital Main", phone: "(603) 669-5300" },
      { type: "The Doorway of Greater Manchester", phone: "(603) 663-4430" },
      { type: "24/7 Statewide Doorway Line", phone: "2-1-1" },
    ],
  },

  {
    id: 163,
    name: "Cheshire Medical Center – The Doorway at Cheshire",
    category: "Primary SUD Providers",
    subcategory: ["Treatment Centers"],
    counties: ["All", "Cheshire"],
    address: "580 Court St, Keene, NH 03431",
    phone: "(603) 354-5400",
    website: "https://www.cheshiremed.org/",
    email: "",
    services: [
      "Substance use disorder screening and assessment",
      "Crisis stabilization and referral to detox",
      "Care coordination for residential and outpatient treatment",
      "Integrated behavioral health services",
      "Linkage to MAT providers and community recovery supports",
    ],
    description:
      "Regional hospital in Keene hosting The Doorway at Cheshire, providing substance use screening, stabilization, and coordinated referrals to treatment services across the Monadnock region.",
    notes:
      "Doorway walk-in location: 24 Railroad St, Keene, NH 03431. Statewide access: dial 2-1-1 for 24/7 support.",
    contacts: [
      { type: "Hospital Main", phone: "(603) 354-5400" },
      { type: "The Doorway", phone: "2-1-1" },
    ],
  },

  {
    id: 164,
    name: "Elliot Hospital – Center for Recovery Management",
    category: "Primary SUD Providers",
    subcategory: ["Treatment Centers"],
    counties: ["All", "Hillsborough"],
    address: "300 Hanover St, Manchester, NH 03104",
    phone: "(603) 663-4430",
    website:
      "https://www.elliothospital.org/locations/center-recovery-management-manchester",
    email: "",
    services: [
      "Outpatient substance use disorder treatment",
      "Medication-Assisted Treatment (MAT)",
      "Partial Hospitalization Program (PHP)",
      "Intensive Outpatient Program (IOP)",
      "Individual, group, and family counseling",
      "Integrated psychiatric and medical care",
      "Addiction psychiatry and clinical medication management",
    ],
    description:
      "Elliot Health System’s specialized outpatient SUD treatment program offering MAT, counseling, PHP, IOP, and integrated behavioral health services for individuals with substance use disorders.",
    notes:
      "Main hospital campus: 1 Elliot Way, Manchester, NH 03103. This program operates from their Hanover Street location.",
    contacts: [
      { type: "Center for Recovery Management", phone: "(603) 663-4430" },
      { type: "Hospital Main", phone: "(603) 669-5300" },
    ],
  },

  {
    id: 165,
    name: "Goodwin Community Health – Behavioral Health & Substance Misuse",
    category: "Primary SUD Providers",
    subcategory: ["Treatment Centers"],
    counties: ["All", "Strafford"],
    address: "311 Route 108, Somersworth, NH 03878",
    phone: "(603) 749-2346",
    website: "https://greaterseacoasthealth.org/services/behavioral-health/",
    email: "",
    services: [
      "Substance misuse counseling for adults and adolescents",
      "Integrated behavioral health within primary care",
      "Brief intervention and relapse prevention",
      "Care coordination and referral to higher levels of treatment",
      "Medication-Assisted Treatment (through partner providers)",
      "Telehealth counseling services",
    ],
    description:
      "Federally qualified health center providing integrated behavioral health and substance misuse services in Somersworth and the Seacoast region, including counseling, care coordination, and MAT linkage.",
    notes:
      "Part of Greater Seacoast Community Health, including Goodwin Community Health and Families First Health Center in Portsmouth.",
    contacts: [{ type: "Clinic Main", phone: "(603) 749-2346" }],
  },

  {
    id: 166,
    name: "Groups Recover Together – New Hampshire",
    category: "Primary SUD Providers",
    subcategory: ["Treatment Centers"],
    statewide: true,
    counties: ["All", "Hillsborough", "Cheshire"],
    address:
      "Manchester, Nashua, and Keene, NH; virtual treatment available statewide",
    phone: "(603) 518-6725",
    website: "https://locations.joingroups.com/nh/",
    email: "",
    services: [
      "Outpatient Medication-Assisted Treatment (MAT) using Suboxone",
      "Group-based addiction counseling",
      "Individual case management and recovery planning",
      "Telehealth for MAT and therapy",
      "Long-term recovery maintenance program",
    ],
    description:
      "Outpatient opioid treatment provider specializing in Suboxone-based MAT combined with structured group counseling and recovery planning, with multiple New Hampshire locations.",
    notes:
      "Manchester: 377 S Willow St, Suite B2-5. Nashua: 155 Main Dunstable Rd, Suite 115. Keene: 12 Kingsbury St. Central intake covers all locations and virtual care.",
    contacts: [
      { type: "Central Intake / New Members", phone: "(603) 518-6725" },
      { type: "Manchester Office", phone: "(603) 565-2798" },
      { type: "Nashua Office", phone: "(603) 965-2203" },
      { type: "Keene Office", phone: "(603) 272-6668" },
    ],
  },

  {
    id: 167,
    name: "Health Care Resource Centers (HCRC) – Hudson",
    category: "Primary SUD Providers",
    subcategory: ["Treatment Centers"],
    counties: ["All", "Hillsborough"],
    address: "323 Derry Rd, Hudson, NH 03051",
    phone: "(603) 453-1983",
    website: "https://hcrcenters.com/locations/new-hampshire/hudson",
    email: "",
    services: [
      "Opioid Treatment Program (methadone maintenance)",
      "Opioid use disorder stabilization and maintenance",
      "Substance use counseling and relapse prevention",
      "Case management and service coordination",
      "Referrals to mental health and social services",
    ],
    description:
      "OTP clinic in Hudson offering methadone-based medication-assisted treatment, counseling, and recovery planning for adults with opioid use disorder.",
    notes:
      "One of three HCRC opioid treatment locations in New Hampshire.",
    contacts: [{ type: "Clinic Main", phone: "(603) 453-1983" }],
  },

  {
    id: 168,
    name: "Reality Check Inc.",
    category: "Primary SUD Providers",
    subcategory: ["Treatment Centers"],
    counties: ["All", "Cheshire"],
    address: "17 Turnpike Rd, Jaffrey, NH 03452",
    phone: "(603) 532-9888",
    website: "https://www.realitychecknow.org/",
    email: "info@realitychecknow.org",
    services: [
      "Substance use prevention and education programming",
      "Community-based substance misuse services",
      "Screening, referral, and treatment navigation",
      "Family education and recovery support",
      "School and community prevention initiatives",
    ],
    description:
      "Nonprofit based in Jaffrey providing prevention, education, and community-facing substance use services, including treatment navigation and referral support across the Monadnock region.",
    notes:
      "Although mainly prevention-focused, they provide direct community-level engagement and referral for SUD treatment services.",
    contacts: [
      { type: "Office", phone: "(603) 532-9888" },
      { type: "General Email", email: "info@realitychecknow.org" },
    ],
  },
  {
    id: 201,
    name: "On the Road to Wellness (OTRTW)",
    category: ["Primary SUD Providers", "Primary Mental Health Providers"],
    subcategory: "PSC",
    counties: ["Hillsborough", "Rockingham"],
    address:
      "Manchester Center: 377 South Willow Street, Suite B2-4, Manchester, NH 03103; Derry Center: 16 Manning Street, Unit 101, Derry, NH 03038",
    phone: "(603) 623-4523 (Manchester), (603) 552-3177 (Derry)",
    website: "https://www.otrtw.org",
    email: "",
    services: [
      "Peer support",
      "Support groups",
      "Residential program",
      "Transportation",
      "Warmline",
      "Mental health recovery",
      "Substance use recovery",
    ],
    description:
      "Peer-led, member-driven organization serving adults 18 and older in mental health and substance use recovery. Offers free, non-clinical peer support with up to six peer-led support groups daily (in-person and via Zoom). Features Intentional Peer Support (IPS) emphasizing connection, mutuality, and personal growth, plus Hope-Based Conversations fostering curiosity, empathy, and positive change. Includes a voluntary 90-day peer-run residential mental health program (Step-Up Step-Down) in Manchester, free weekday transportation for participants (8:30am–4:30pm), and confidential Warmline services (5pm–10pm). All services provided without insurance, identification, or payment requirements.",
    notes:
      "Founded in 1988. Manchester and Derry center hours: Monday–Thursday 8:30 AM–5:30 PM; Friday 8:30 AM–4:30 PM; closed weekends and holidays. No appointment, identification, insurance, or payment is required for peer-center participation.",
  },
  {
    id: 366,
    name: "Dartmouth Hitchcock Addiction Treatment Program (ATP)",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["All", "Grafton"],
    address: "85 Mechanic St, Suite B3-1, Lebanon, NH 03766",
    phone: "(603) 650-5000",
    website:
      "https://www.dartmouth-hitchcock.org/psychiatry/addiction-treatment-program",
    email: "",
    services: [
      "Comprehensive SUD Evaluation",
      "Intensive Outpatient Program (IOP)",
      "Individual Outpatient Therapy",
      "Medication-Assisted Treatment (MAT)",
      "Behavioral Health Navigation",
      "The Doorway at DHMC (SUD screening, care coordination, naloxone)",
      "Moms in Recovery Program (perinatal)",
    ],
    description:
      "Dartmouth Hitchcock Medical Center's Addiction Treatment Program provides evidence-based outpatient evaluation and treatment for adults 18+ with substance use disorders. The program also houses The Doorway at DHMC, one of nine statewide Doorway hubs, offering SUD screening, crisis support, MAT initiation, recovery coaching, and care coordination.",
    notes:
      "ATP and The Doorway are located at 85 Mechanic St in Lebanon. The Doorway is in Suite B3-1. Moms in Recovery serves pregnant and parenting people with opioid use disorder.",
    contacts: [
      { type: "ATP General", phone: "(603) 650-5000" },
      { type: "The Doorway at DHMC", phone: "2-1-1" },
    ],
  },
  {
    id: 203,
    name: "Manchester Comprehensive Treatment Center (CTC)",
    category: "Primary SUD Providers",
    subcategory: ["Treatment Centers"],
    counties: ["All", "Hillsborough"],
    address: "20 Market Street, Manchester, NH 03101",
    phone: "(603) 622-4747",
    website:
      "https://www.ctcprograms.com/location/manchester-comprehensive-treatment-center/",
    email: "",
    services: [
      "Opioid Treatment Program (OTP)",
      "Methadone treatment",
      "Medication-Assisted Treatment (MAT)",
      "Outpatient counseling",
      "Drug-free treatment options",
      "Individualized recovery planning",
    ],
    description:
      "Opioid Treatment Program (OTP) providing methadone treatment along with outpatient counseling services. Also offers drug-free treatment options depending on each person's needs and recovery goals. Part of the Comprehensive Treatment Centers network under Acadia Healthcare.",
    notes:
      "24/7 appointment scheduling available. Serves Manchester and surrounding communities.",
    contacts: [{ type: "Main", phone: "(603) 622-4747" }],
  },
  {
    id: 400,
    name: "Health Care Resource Centers (HCRC) – Newington",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["Rockingham"],
    address: "177 Shattuck Way, Newington, NH 03801",
    phone: "(603) 207-0015",
    website: "https://hcrcenters.com/locations/new-hampshire/newington",
    email: "",
    services: [
      "Methadone and buprenorphine treatment",
      "Substance use counseling",
      "Case management",
      "Trauma-informed care",
    ],
    description:
      "Outpatient opioid treatment program providing medication, counseling, and recovery support.",
    notes: "One of three HCRC locations in New Hampshire.",
  },
  {
    id: 401,
    name: "Health Care Resource Centers (HCRC) – Somersworth",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["Strafford"],
    address: "200 NH-108, Somersworth, NH 03878",
    phone: "(603) 453-1539",
    website: "https://hcrcenters.com/locations/new-hampshire",
    email: "",
    services: [
      "Methadone and buprenorphine treatment",
      "Substance use counseling",
      "Case management",
      "Trauma-informed care",
    ],
    description:
      "Outpatient opioid treatment program providing medication, counseling, and recovery support.",
    notes: "One of three HCRC locations in New Hampshire.",
  },
  {
    id: 402,
    name: "New Season – Concord Metro Treatment Center",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["Merrimack"],
    address: "100 Hall St, Concord, NH 03301",
    phone: "(603) 229-4260",
    website:
      "https://www.newseason.com/treatment-center-locations/new-hampshire/",
    email: "",
    services: [
      "Methadone, buprenorphine, and Suboxone treatment",
      "Opioid use disorder counseling",
      "Outpatient recovery planning",
    ],
    description:
      "Doctor-led outpatient opioid treatment combining medication and counseling.",
    notes: "",
  },
  {
    id: 403,
    name: "New Season Treatment Center – Franklin",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["Merrimack"],
    address: "880 Central St, Suite K, Franklin, NH 03235",
    phone: "(603) 671-3215",
    website:
      "https://www.newseason.com/treatment-center-locations/new-hampshire/",
    email: "",
    services: [
      "Methadone, buprenorphine, and Suboxone treatment",
      "Opioid use disorder counseling",
      "Outpatient recovery planning",
    ],
    description:
      "Doctor-led outpatient opioid treatment combining medication and counseling.",
    notes: "",
  },
  {
    id: 404,
    name: "New Season – Keene Metro Treatment Center",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["Cheshire"],
    address: "1076 W Swanzey Rd, Swanzey, NH 03446",
    phone: "(603) 358-0050",
    website:
      "https://www.newseason.com/treatment-center-locations/new-hampshire/",
    email: "",
    services: [
      "Methadone, buprenorphine, and Suboxone treatment",
      "Opioid use disorder counseling",
      "Outpatient recovery planning",
    ],
    description:
      "Doctor-led outpatient opioid treatment combining medication and counseling.",
    notes: "",
  },
  {
    id: 405,
    name: "New Season – Manchester Metro Treatment Center",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["Hillsborough"],
    address: "228 Maple St, Manchester, NH 03103",
    phone: "(603) 622-5005",
    website:
      "https://www.newseason.com/treatment-center-locations/new-hampshire/",
    email: "",
    services: [
      "Methadone, buprenorphine, and Suboxone treatment",
      "Opioid use disorder counseling",
      "Outpatient recovery planning",
    ],
    description:
      "Doctor-led outpatient opioid treatment combining medication and counseling.",
    notes: "",
  },
  {
    id: 406,
    name: "New Season Treatment Center – Manchester West",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["Hillsborough"],
    address: "865 Second St, Units 1B, 2 and 3, Manchester, NH 03102",
    phone: "(603) 397-0970",
    website:
      "https://www.newseason.com/treatment-center-locations/new-hampshire/",
    email: "",
    services: [
      "Methadone, buprenorphine, and Suboxone treatment",
      "Opioid use disorder counseling",
      "Outpatient recovery planning",
    ],
    description:
      "Doctor-led outpatient opioid treatment combining medication and counseling.",
    notes: "",
  },
  {
    id: 407,
    name: "H.A.L.O. Educational Systems",
    category: ["Primary SUD Providers", "Primary Mental Health Providers"],
    subcategory: "Treatment Centers",
    counties: ["Grafton"],
    address: "44 Roberts Rd, Canaan, NH 03741",
    phone: "(603) 523-8804",
    website: "https://haloeducationalsystems.com/our-services",
    email: "",
    services: [
      "Mental health and addiction counseling",
      "Intensive outpatient program",
      "Individual outpatient services",
      "LADC evaluations and impaired-driver services",
      "Telehealth",
    ],
    description:
      "Integrated behavioral health provider offering outpatient mental health and substance use treatment in the Upper Valley.",
    notes:
      "Telehealth and some Lebanon-area programming are available; confirm current eligibility and meeting location.",
  },
  {
    id: 408,
    name: "North Country Healthcare Recovery – Colebrook",
    category: ["Primary SUD Providers", "Primary Mental Health Providers"],
    subcategory: "Treatment Centers",
    counties: ["Coös"],
    address: "141 Corliss Ln, Colebrook, NH 03576",
    phone: "(603) 788-5075",
    website: "https://northcountryhealth.org/department/addiction-recovery/",
    email: "",
    services: [
      "Substance use assessment and therapy",
      "Medication-assisted treatment",
      "Co-occurring behavioral health care",
      "Recovery support",
    ],
    description:
      "North Country Healthcare addiction and behavioral health treatment location.",
    notes: "Appointments are coordinated through NCH Recovery.",
  },
  {
    id: 409,
    name: "North Country Healthcare Recovery – Lancaster",
    category: ["Primary SUD Providers", "Primary Mental Health Providers"],
    subcategory: "Treatment Centers",
    counties: ["Coös"],
    address: "173 Middle St, Lancaster, NH 03584",
    phone: "(603) 788-5075",
    website: "https://northcountryhealth.org/department/addiction-recovery/",
    email: "",
    services: [
      "Substance use assessment and therapy",
      "Medication-assisted treatment",
      "Co-occurring behavioral health care",
      "Recovery support",
    ],
    description:
      "North Country Healthcare addiction and behavioral health treatment location.",
    notes: "Appointments are coordinated through NCH Recovery.",
  },
  {
    id: 410,
    name: "North Country Healthcare Recovery – Littleton",
    category: ["Primary SUD Providers", "Primary Mental Health Providers"],
    subcategory: "Treatment Centers",
    counties: ["Grafton"],
    address: "536 Cottage St, Littleton, NH 03561",
    phone: "(603) 788-5075",
    website: "https://northcountryhealth.org/department/addiction-recovery/",
    email: "",
    services: [
      "Substance use assessment and therapy",
      "Medication-assisted treatment",
      "Co-occurring behavioral health care",
      "Recovery support",
    ],
    description:
      "North Country Healthcare addiction and behavioral health treatment location.",
    notes: "Appointments are coordinated through NCH Recovery.",
  },
  {
    id: 411,
    name: "North Country Healthcare Recovery – Whitefield",
    category: ["Primary SUD Providers", "Primary Mental Health Providers"],
    subcategory: "Treatment Centers",
    counties: ["Coös"],
    address: "8 Clover Ln, Whitefield, NH 03598",
    phone: "(603) 788-5075",
    website: "https://northcountryhealth.org/department/addiction-recovery/",
    email: "",
    services: [
      "Substance use assessment and therapy",
      "Medication-assisted treatment",
      "Co-occurring behavioral health care",
      "Recovery support",
    ],
    description:
      "North Country Healthcare addiction and behavioral health treatment location.",
    notes: "Appointments are coordinated through NCH Recovery.",
  },
  {
    id: 412,
    name: "West Lebanon Comprehensive Treatment Center",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["Grafton"],
    address: "254 N Plainfield Rd, Unit 5, West Lebanon, NH 03784",
    phone: "(855) 951-2398",
    website:
      "https://www.ctcprograms.com/location/west-lebanon-comprehensive-treatment-center/",
    email: "",
    services: [
      "Methadone, buprenorphine, and Suboxone treatment",
      "Outpatient opioid use disorder counseling",
      "Medication-assisted treatment assessment",
    ],
    description:
      "Outpatient opioid treatment program for adults combining medication and counseling.",
    notes: "",
  },
  {
    id: 413,
    name: "SaVida Health – Gilford",
    category: ["Primary SUD Providers", "Primary Mental Health Providers"],
    subcategory: "Treatment Centers",
    counties: ["Belknap"],
    address: "401 Gilford Ave, Gilford, NH 03249",
    phone: "(603) 556-4409",
    website: "https://savidahealth.com/locations/",
    email: "",
    services: [
      "Medication-assisted treatment",
      "Individual and group therapy",
      "Psychiatric medication management",
      "Care coordination",
    ],
    description:
      "Outpatient addiction treatment with medication, counseling, and co-occurring behavioral health care.",
    notes: "",
  },
  {
    id: 414,
    name: "SaVida Health – Manchester",
    category: ["Primary SUD Providers", "Primary Mental Health Providers"],
    subcategory: "Treatment Centers",
    counties: ["Hillsborough"],
    address: "60 Rogers St, Suite 215, Manchester, NH 03103",
    phone: "(603) 836-0620",
    website: "https://savidahealth.com/locations/",
    email: "",
    services: [
      "Medication-assisted treatment",
      "Individual and group therapy",
      "Psychiatric medication management",
      "Care coordination",
    ],
    description:
      "Outpatient addiction treatment with medication, counseling, and co-occurring behavioral health care.",
    notes: "",
  },
  {
    id: 415,
    name: "Behavioral Health Services by Easterseals NH",
    category: ["Primary SUD Providers", "Primary Mental Health Providers"],
    subcategory: "Treatment Centers",
    counties: ["Hillsborough", "Merrimack"],
    address:
      "700 Lake Ave, Manchester, NH 03103; 14 Holy Cross Rd, Franklin, NH 03235",
    phone: "(603) 207-2434",
    website: "https://behavioralhealthnh.org/get-help/",
    email: "",
    services: [
      "Outpatient substance use treatment",
      "Intensive outpatient programming",
      "Mental health counseling",
      "LADC and impaired-driver evaluations",
      "Telehealth after initial in-person assessment when appropriate",
    ],
    description:
      "Current name for the former Farnum outpatient program, serving adults, veterans, first responders, and families.",
    notes:
      "Services are offered in Manchester and Franklin. Telehealth availability depends on the service.",
  },
  {
    id: 416,
    name: "Forge Health – Manchester",
    category: ["Primary SUD Providers", "Primary Mental Health Providers"],
    subcategory: "Treatment Centers",
    statewide: true,
    counties: ["Hillsborough"],
    address: "1750 Elm St, Suite 103, Manchester, NH 03104",
    phone: "(603) 865-1706",
    website: "https://forgehealth.com/location-offices/manchester/",
    email: "info@forgehealth.com",
    services: [
      "Mental health and substance use treatment",
      "Intensive outpatient programming",
      "Psychiatry and medication management",
      "Trauma, veteran, and first-responder services",
      "Statewide telehealth",
    ],
    description:
      "Integrated outpatient behavioral health provider with an office in Manchester and telehealth throughout New Hampshire.",
    notes: "",
  },
  {
    id: 417,
    name: "Harbor Care Health & Wellness Center",
    category: ["Primary SUD Providers", "Primary Mental Health Providers"],
    subcategory: "Treatment Centers",
    counties: ["Hillsborough"],
    address: "45 High St, Nashua, NH 03060",
    phone: "(603) 821-7788",
    website: "https://harborcarenh.org/health-and-wellness-center/",
    email: "",
    services: [
      "Substance use disorder treatment",
      "Psychiatry and mental health care",
      "Integrated primary care",
      "Telehealth",
    ],
    description:
      "Integrated health center providing behavioral health, substance use, primary care, dental, and pharmacy services.",
    notes:
      "Face-to-face care is offered through Nashua locations; telehealth is also available.",
  },
  {
    id: 418,
    name: "Lamprey Health Care – Behavioral Health & SUD Services",
    category: ["Primary SUD Providers", "Primary Mental Health Providers"],
    subcategory: "Treatment Centers",
    counties: ["Hillsborough", "Rockingham"],
    address:
      "22 Prospect St, Nashua; 207 S Main St, Newmarket; 128 Route 27, Raymond",
    phone: "",
    website: "https://lampreyhealth.org/",
    email: "",
    services: [
      "Integrated behavioral health",
      "Substance use disorder services",
      "Medication-assisted treatment",
      "Primary care and mobile health",
    ],
    description:
      "Federally qualified health center integrating mental health and substance use services at three New Hampshire clinics.",
    notes:
      "Nashua is in Hillsborough County; Newmarket and Raymond are in Rockingham County.",
  },
  {
    id: 419,
    name: "Community Improvement Associates",
    category: ["Primary SUD Providers", "Primary Mental Health Providers"],
    subcategory: "Treatment Centers",
    counties: ["Cheshire", "Hillsborough"],
    address:
      "160 Emerald St, Suite 203, Keene, NH 03431; 25 Bay St, Manchester, NH 03103",
    phone: "(603) 352-1016",
    website: "https://cianh.com/services/",
    email: "",
    services: [
      "Substance use and mental health counseling",
      "Intensive outpatient program",
      "Medication-assisted treatment",
      "Impaired-driver case management",
      "Telehealth",
    ],
    description:
      "Behavioral health provider offering outpatient treatment, IOP, MAT, and court-related programs.",
    notes: "The IOP and MAT programs are based at the Keene facility.",
  },
  {
    id: 420,
    name: "Dismas Home of New Hampshire",
    category: ["Housing", "Primary SUD Providers"],
    subcategory: ["Recovery Homes", "Treatment Centers"],
    counties: ["Hillsborough"],
    address: "Manchester, NH 03102",
    phone: "(603) 782-3004",
    website: "https://dismashomenh.org/program/",
    email: "info@dismashomenh.org",
    services: [
      "90-day low-intensity residential treatment",
      "Transitional living",
      "Individual and group therapy",
      "Recovery support and case management",
    ],
    description:
      "Residential recovery and independent-living program for New Hampshire women with substance use disorder and justice-system involvement.",
    notes: "Contact the program for its current residential address and referral requirements.",
  },
  {
    id: 421,
    name: "Amoskeag Health – ProHealth Integrated Care",
    category: ["Primary SUD Providers", "Primary Mental Health Providers"],
    subcategory: "Treatment Centers",
    counties: ["Hillsborough"],
    address: "2 Wall St, Manchester, NH 03101",
    phone: "(603) 626-9500",
    website: "https://www.amoskeaghealth.org/pt/prohealth",
    email: "",
    services: [
      "Integrated primary and mental health care",
      "Counseling",
      "Substance use services",
      "Care coordination",
      "On-site pharmacy",
    ],
    description:
      "Partnership between Amoskeag Health and the Mental Health Center of Greater Manchester for patients age 12 and older.",
    notes: "",
  },
  {
    id: 422,
    name: "Confidant Health – Virtual New Hampshire Care",
    category: ["Primary SUD Providers", "Primary Mental Health Providers"],
    subcategory: "Treatment Centers",
    statewide: true,
    counties: [],
    address: "Virtual services throughout New Hampshire",
    phone: "",
    website: "https://www.confidanthealth.com/for-patients",
    email: "",
    services: [
      "Virtual mental health and addiction treatment",
      "Therapy and recovery coaching",
      "Psychiatric medication management",
      "Virtual medication treatment for opioid and alcohol use disorders",
      "Online support groups",
    ],
    description:
      "Virtual behavioral health practice offering clinical mental health and substance use services to New Hampshire residents.",
    notes: "Clinical services are available in New Hampshire through the Confidant app.",
  },
  {
    id: 423,
    name: "Aspire365",
    category: ["Primary SUD Providers", "Primary Mental Health Providers"],
    subcategory: "Treatment Centers",
    statewide: true,
    counties: [],
    address: "In-home and telehealth services available in New Hampshire",
    phone: "(888) 678-6960",
    website: "https://aspire365.com/",
    email: "info@aspire365.com",
    services: [
      "In-home and telehealth behavioral health treatment",
      "Mental health and substance use treatment",
      "Co-occurring disorder care",
      "Adolescent and adult programming",
      "Family-centered treatment",
    ],
    description:
      "Long-term, team-based behavioral health care delivered through telehealth and in-home visits.",
    notes: "New Hampshire is listed as an active Aspire365 service state.",
  },
  {
    id: 424,
    name: "Middlesex Recovery – Salem",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: ["Rockingham"],
    address: "6 Manor Pkwy, Suite 201, Salem, NH 03079",
    phone: "(603) 757-5643",
    website: "https://www.middlesexrecovery.com/locations/salem/",
    email: "",
    services: [
      "Medication-assisted treatment",
      "Opioid and alcohol use disorder treatment",
      "Ambulatory detoxification",
      "Case management and counseling referrals",
    ],
    description:
      "Outpatient medication treatment for opioid and alcohol use disorders in Salem.",
    notes: "The Salem clinic relocated to this address in April 2026.",
  },
  {
    id: 425,
    name: "Dartmouth Hitchcock – Adult and Child Psychiatry",
    category: "Primary Mental Health Providers",
    counties: ["Grafton"],
    address:
      "One Medical Center Dr, 5th Floor, Reception 5D, Lebanon, NH 03756",
    phone: "(603) 650-7075",
    website:
      "https://www.dartmouth-hitchcock.org/psychiatry/adult-outpatient-treatments-services",
    email: "",
    services: [
      "Adult and child outpatient psychiatry",
      "Psychotherapy and medication management",
      "Anxiety and trauma treatment",
      "Neuropsychological testing",
      "Mood and interventional psychiatry",
    ],
    description:
      "Dartmouth Health psychiatric evaluation and specialty mental health treatment in Lebanon.",
    notes:
      "Dartmouth also provides inpatient psychiatric and emergency behavioral health services through its hospital system.",
  },
  {
    id: 426,
    name: "Elliot Behavioral Health",
    category: "Primary Mental Health Providers",
    counties: ["Hillsborough"],
    address:
      "1 Elliot Way and 445 Cypress St, Suite 8, Manchester, NH 03103",
    phone: "(603) 668-4079",
    website:
      "https://www.elliothospital.org/services/browse-all-services/behavioral-health",
    email: "",
    services: [
      "Inpatient and outpatient behavioral health",
      "Adult, child, and adolescent services",
      "Psychiatric emergency evaluation",
      "Co-occurring mental health and substance use programs",
      "Partial hospitalization and intensive outpatient care",
    ],
    description:
      "Elliot Health System’s hospital and outpatient behavioral health continuum in Manchester.",
    notes:
      "The hospital is at 1 Elliot Way; outpatient behavioral health is at 445 Cypress St.",
  },
  {
    id: 427,
    name: "Southern New Hampshire Health – Behavioral Health",
    category: "Primary Mental Health Providers",
    counties: ["Hillsborough"],
    address: "29 Northwest Blvd, Nashua, NH 03063",
    phone: "(603) 577-2000",
    website:
      "https://www.snhhealth.org/locations/southern-nh-medical-center-west-campus",
    email: "",
    services: [
      "Inpatient behavioral health",
      "Partial hospitalization",
      "Intensive outpatient programming",
      "Outpatient behavioral health",
      "Behavioral health assessment and referral",
    ],
    description:
      "Southern New Hampshire Health behavioral care continuum, with outpatient services at the West Campus.",
    notes:
      "Hospital-based inpatient and emergency services operate through Southern New Hampshire Medical Center.",
  },
  {
    id: 428,
    name: "Portsmouth Regional Hospital – Behavioral Health",
    category: "Primary Mental Health Providers",
    counties: ["Rockingham"],
    address: "333 Borthwick Ave, Portsmouth, NH 03801",
    phone: "(603) 433-5270",
    website: "https://www.portsmouthhospital.com/specialties/mental-health-and-wellness",
    email: "",
    services: [
      "Inpatient psychiatric treatment",
      "Psychiatric assessment and referral",
      "Adult partial hospitalization",
      "Medication evaluation",
      "Group, individual, and family interventions",
    ],
    description:
      "Hospital-based inpatient and outpatient psychiatric treatment for adults in the Seacoast region.",
    notes:
      "The adult partial hospitalization program may operate at a separate Hampton location.",
  },
  {
    id: 429,
    name: "St. Joseph Hospital – Senior Behavioral Health Unit",
    category: "Primary Mental Health Providers",
    counties: ["Hillsborough"],
    address: "172 Kinsley St, Nashua, NH 03060",
    phone: "(603) 884-5550",
    website:
      "https://stjosephhospital.com/locations/st-joseph-hospital-senior-behavioral-health-unit/",
    email: "",
    services: [
      "Inpatient geriatric psychiatric care",
      "Behavioral health assessment",
      "Medication stabilization",
      "Discharge planning",
    ],
    description:
      "Inpatient behavioral health program specializing in the needs of older adults.",
    notes: "Referrals are accepted around the clock.",
  },
  {
    id: 430,
    name: "MAPS Counseling Services",
    category: ["Primary Mental Health Providers", "Primary SUD Providers"],
    subcategory: "Treatment Centers",
    counties: ["Cheshire", "Hillsborough"],
    address:
      "23 Central Sq, Suite 300, Keene; 174 Concord St, Suite 250, Peterborough",
    phone: "(603) 355-2244",
    website: "https://mapsnh.org/",
    email: "office@mapsnh.org",
    services: [
      "Outpatient psychotherapy",
      "Child, adolescent, adult, and family services",
      "Substance misuse counseling",
      "Care for uninsured clients",
    ],
    description:
      "Nonprofit outpatient counseling center serving children, adults, and families in the Monadnock region.",
    notes:
      "Peterborough office: (603) 924-2240. The Hope and Healing Fund may help reduce financial barriers.",
  },
  {
    id: 431,
    name: "Serene Behavioral Healthcare",
    category: "Primary Mental Health Providers",
    counties: ["Hillsborough"],
    address: "1045 Elm St, HQ Suite 204, Manchester, NH 03101",
    phone: "(603) 403-3975",
    website: "https://www.serenebhc.com/booking",
    email: "admin@serenebhc.com",
    services: [
      "Child, adolescent, and adult psychiatry",
      "Psychiatric evaluation",
      "Medication management",
      "Behavioral health treatment",
    ],
    description:
      "Manchester psychiatric practice serving children, adolescents, and adults.",
    notes: "",
  },
  {
    id: 432,
    name: "The Wediko School",
    category: "Primary Mental Health Providers",
    counties: ["Hillsborough"],
    address: "11 Bobcat Blvd, Windsor, NH 03244",
    phone: "(603) 824-8444",
    website: "https://thewedikoschool.org/",
    email: "wediko@thehome.org",
    services: [
      "Residential behavioral treatment",
      "Individual and family therapy",
      "Therapeutic education",
      "Residential life and academic support",
    ],
    description:
      "Residential treatment and educational program for middle- and high-school-aged boys with significant social, emotional, behavioral, and educational challenges.",
    notes: "Contact admissions for referral and eligibility requirements.",
  },
  {
    id: 433,
    name: "Pine Haven Boys Center",
    category: "Primary Mental Health Providers",
    counties: ["Merrimack"],
    address: "133 River Rd, Allenstown, NH 03275",
    phone: "(603) 485-7141",
    website: "https://pinehavenboyscenter.org/",
    email: "info@pinehavenboyscenter.org",
    services: [
      "Intensive residential care",
      "Clinical services",
      "Special education",
      "Family-focused programming",
      "Respite family program",
    ],
    description:
      "Nonprofit residential, clinical, and educational program for boys ages 6 to 15 with behavioral, educational, or emotional needs.",
    notes: "Admissions are referral-based.",
  },
  {
    id: 434,
    name: "Stepping Stone",
    category: ["Primary Mental Health Providers", "Primary SUD Providers"],
    subcategory: "PSC",
    counties: ["Sullivan"],
    address: "108 Pleasant St, Claremont, NH 03743",
    phone: "(603) 543-1388",
    website: "https://www.steppingstonenextstep.org/",
    email: "susan.seidler@steppingstonenextstep.org",
    services: [
      "Free peer support",
      "Peer-led recovery activities",
      "Individual support",
      "Warmline",
    ],
    description:
      "Peer-led, non-medical mental health wellness and recovery center serving the Claremont and River Valley region.",
    notes: "Sister program to Next Step in Lebanon.",
  },
  {
    id: 435,
    name: "Dartmouth Hitchcock Addiction Treatment Program – Claremont",
    category: ["Primary SUD Providers", "Primary Mental Health Providers"],
    subcategory: "Treatment Centers",
    counties: ["Sullivan"],
    address: "7 Dunning St, Claremont, NH 03743",
    phone: "(603) 542-5043",
    website:
      "https://www.dartmouth-hitchcock.org/psychiatry/addiction-treatment-program",
    email: "",
    services: [
      "Comprehensive substance use evaluation",
      "Individual and group outpatient therapy",
      "Medication for addiction treatment",
      "Co-occurring mental health treatment",
      "Care coordination",
    ],
    description:
      "Dartmouth Health outpatient addiction treatment program serving Claremont and Sullivan County.",
    notes: "",
  },
  {
    id: 436,
    name: "Manchester Pathways – A Clubhouse",
    category: "Primary Mental Health Providers",
    counties: ["All", "Hillsborough"],
    address: "60 Rogers St, Suite 204, Manchester, NH 03103",
    phone: "(603) 263-1300",
    website: "https://www.manchesterclubhouse.org/",
    email: "info@granitepathways.org",
    services: [
      "Mental health and brain injury peer community",
      "Employment and education support",
      "Social and recreational activities",
      "Skills development and structured clubhouse work",
    ],
    description:
      "Supportive clubhouse community for adults with a mental health diagnosis or brain injury.",
    notes:
      "Open Monday through Friday; contact the clubhouse to schedule a tour.",
  },
  {
    id: 437,
    name: "Seacoast Pathways – A Clubhouse",
    category: "Primary Mental Health Providers",
    counties: ["All", "Rockingham", "Strafford"],
    address: "155 Brewery Ln, Suite 102, Portsmouth, NH 03801",
    phone: "(603) 570-9804",
    website: "https://granitepathwaysnh.org/history-seacoastpathways/",
    email: "seacoastpathways@gmail.com",
    services: [
      "Mental health and brain injury peer community",
      "Employment and education support",
      "Social and recreational activities",
      "Psychosocial rehabilitation through the clubhouse model",
    ],
    description:
      "Clubhouse community serving adults with mental illness or brain injury in the New Hampshire Seacoast area.",
    notes:
      "A clinical referral is currently required for membership; contact the program before visiting.",
  },
  {
    id: 438,
    name: "Ammonoosuc Community Health Services – Behavioral Health & SUD",
    category: [
      "Primary SUD Providers",
      "Primary Mental Health Providers"
    ],
    subcategory: "Treatment Centers",
    counties: [
      "Grafton",
      "Coös"
    ],
    statewide: false,
    address: "25 Mt. Eustis Road, Littleton, NH 03561",
    phone: "(603) 444-2464",
    website: "https://www.ammonoosuc.org/services/",
    email: "",
    services: [
      "Behavioral health counseling",
      "Substance use treatment",
      "Medication-assisted treatment",
      "Care coordination"
    ],
    description: "Community health center offering integrated behavioral health and substance use care in northern New Hampshire.",
    notes: "Contact ACHS to establish care and identify the appropriate clinic. Financial assistance is available; services are not denied because of inability to pay."
  },
  {
    id: 439,
    name: "Catholic Charities New Hampshire – Mental Health Counseling",
    category: "Therapists",
    counties: [
      "Grafton",
      "Coös",
      "Hillsborough"
    ],
    statewide: true,
    address: "Manchester, Nashua, Littleton and Lancaster, NH",
    phone: "1-800-562-5249",
    website: "https://www.cc-nh.org/services/mental-health-counseling/",
    email: "",
    services: [
      "Individual counseling",
      "Family and relationship support",
      "Grief and addiction-related counseling",
      "Statewide teletherapy"
    ],
    description: "Mental health counseling for New Hampshire residents of all backgrounds and faiths, with in-person and telehealth options.",
    notes: "Request counseling through the official program page. Accepts many insurance plans and offers sliding-scale fees."
  },
  {
    id: 440,
    name: "White Mountain Community Health Center – Substance Use Treatment",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: [
      "Carroll"
    ],
    statewide: false,
    address: "Conway, NH",
    phone: "(603) 447-8900",
    website: "https://www.whitemountainhealth.org/services/substance-use-disorder-treatment",
    email: "",
    services: [
      "Medication-assisted treatment",
      "Mental health counseling",
      "Recovery coaching",
      "Case management"
    ],
    description: "Outpatient treatment for opioid, stimulant and alcohol use with coordinated medical, counseling and recovery support.",
    notes: "Call or walk in to begin intake. Sliding-scale fees are available for eligible uninsured or underinsured patients."
  },
  {
    id: 441,
    name: "Trailhead Treatment Center",
    category: [
      "Primary SUD Providers",
      "Primary Mental Health Providers"
    ],
    subcategory: "Treatment Centers",
    counties: [
      "Rockingham"
    ],
    statewide: false,
    address: "103 Stiles Road, Suite 102, Salem, NH 03079",
    phone: "(603) 207-1033",
    website: "https://trailheadbh.com/",
    email: "",
    services: [
      "Partial hospitalization",
      "Intensive outpatient treatment",
      "Outpatient therapy",
      "Medication for addiction treatment",
      "Co-occurring mental health care"
    ],
    description: "Salem outpatient provider treating substance use and mental health conditions in adults.",
    notes: "Contact admissions for assessment, scheduling and insurance verification. Outpatient programs do not provide overnight inpatient care."
  },
  {
    id: 442,
    name: "BayPoint Health",
    category: [
      "Primary SUD Providers",
      "Primary Mental Health Providers"
    ],
    subcategory: "Treatment Centers",
    counties: [
      "Rockingham"
    ],
    statewide: false,
    address: "30 International Drive, Suite 101, Portsmouth, NH 03801",
    phone: "(603) 941-4458",
    website: "https://baypointhealth.com/",
    email: "",
    services: [
      "Partial hospitalization",
      "Intensive outpatient treatment",
      "Mental health therapy",
      "Substance use and dual-diagnosis treatment"
    ],
    description: "Portsmouth provider offering structured outpatient mental health and addiction treatment.",
    notes: "Contact admissions for an assessment and insurance review. PHP and IOP allow participants to return home rather than stay overnight."
  },
  {
    id: 443,
    name: "Bresnahan and Ball Counseling Services",
    category: [
      "Primary SUD Providers",
      "Therapists"
    ],
    subcategory: "Treatment Centers",
    counties: [
      "Rockingham"
    ],
    statewide: false,
    address: "1 B Commons Drive, Suite 7, Londonderry, NH 03053",
    phone: "(603) 965-6477",
    website: "https://www.bresnahanandballcounselingservices.com/outpatient-services",
    email: "",
    services: [
      "Substance use assessments",
      "Individual and group counseling",
      "Impaired-driver services",
      "Court-related evaluations"
    ],
    description: "Londonderry practice providing substance use counseling and evaluation services for adults and young people.",
    notes: "Services are offered in English and Spanish. Confirm the required evaluation or counseling service and payment arrangements before scheduling."
  },
  {
    id: 444,
    name: "MaineHealth Memorial Hospital – Comprehensive Addiction Medicine",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: [
      "Carroll"
    ],
    statewide: false,
    address: "3073 White Mountain Highway, North Conway, NH 03860",
    phone: "(603) 356-5472",
    website: "https://www.mainehealth.org/news/2024/06/comprehensive-addiction-medicine-program-goes-full-time-memorial",
    email: "",
    services: [
      "Outpatient addiction medicine",
      "Substance use assessment",
      "Treatment planning",
      "Recovery care within primary care"
    ],
    description: "North Conway addiction medicine program within Memorial Hospital primary care.",
    notes: "Self-referrals and referrals from primary care or community support providers are accepted. Call the primary care team to access the program."
  },
  {
    id: 445,
    name: "MaineHealth New Life Program – North Conway",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: [
      "Carroll"
    ],
    statewide: false,
    address: "3073 White Mountain Highway, North Conway, NH 03860",
    phone: "(603) 356-9355",
    website: "https://www.mainehealth.org/locations/mainehealth-new-life-program-north-conway",
    email: "",
    services: [
      "Prenatal substance use treatment",
      "Integrated obstetric and addiction care",
      "Group counseling",
      "Social work and care coordination"
    ],
    description: "Specialized program coordinating pregnancy care and substance use treatment in North Conway.",
    notes: "Call the nurse coordinator to arrange intake. This pregnancy-specific pathway is separate from general outpatient addiction medicine."
  },
  {
    id: 446,
    name: "Cottage Hospital – Ray of Hope",
    category: "Primary Mental Health Providers",
    counties: [
      "Grafton"
    ],
    statewide: false,
    address: "90 Swiftwater Road, Woodsville, NH 03785",
    phone: "(603) 747-9000",
    website: "https://cottagehospital.org/contact",
    email: "",
    services: [
      "Inpatient behavioral health",
      "Geriatric mental health assessment",
      "Psychiatric treatment and stabilization"
    ],
    description: "Hospital-based Ray of Hope program providing specialized mental health care for older adults.",
    notes: "Call the hospital for referral and admission information; admission requires clinical assessment. The linked hospital contact page identifies the Ray of Hope service."
  },
  {
    id: 447,
    name: "Monadnock Community Hospital – Behavioral Health",
    category: "Primary Mental Health Providers",
    counties: [
      "Hillsborough",
      "Cheshire"
    ],
    statewide: false,
    address: "Monadnock Community Hospital campus, Peterborough, NH",
    phone: "(603) 924-4690",
    website: "https://monadnockcommunityhospital.com/services/behavioral-health-services/",
    email: "",
    services: [
      "Outpatient psychotherapy",
      "Medication evaluation and monitoring",
      "Psychological testing",
      "Individual, family and group counseling"
    ],
    description: "Hospital-affiliated outpatient behavioral health services for children, adults and older adults in the Monadnock region.",
    notes: "An MCH primary care provider is required. New patients must request a referral through their MCH primary care provider."
  },
  {
    id: 448,
    name: "Antioch Psychological Services Center",
    category: "Therapists",
    counties: [
      "Cheshire"
    ],
    statewide: false,
    address: "40 Avon Street, Keene, NH 03431",
    phone: "(603) 352-1024",
    website: "https://www.antioch.edu/centers-institutes/psychological-services-center/appointments-fees/",
    email: "",
    services: [
      "Psychotherapy",
      "Psychological assessment",
      "Individual and family support"
    ],
    description: "University training clinic providing affordable psychological services in the Keene community.",
    notes: "Fees use an income-based sliding scale. The training clinic does not bill insurance directly; contact the clinic for intake and suitability."
  },
  {
    id: 449,
    name: "Center for New Beginnings",
    category: "Therapists",
    counties: [
      "Grafton"
    ],
    statewide: false,
    address: "229 Cottage Street, Littleton, NH 03561",
    phone: "(603) 444-6465",
    website: "https://www.centerfornewbeginnings.org/",
    email: "",
    services: [
      "Individual counseling",
      "Family counseling",
      "Mental health treatment"
    ],
    description: "Littleton nonprofit counseling center serving individuals and families of all ages.",
    notes: "Contact the center for referrals, appointments and payment information."
  },
  {
    id: 450,
    name: "Granite State Children's Alliance – Behavioral Health",
    category: "Primary Mental Health Providers",
    counties: [
      "Belknap",
      "Cheshire",
      "Hillsborough"
    ],
    statewide: false,
    address: "607 Chestnut Street, Manchester, NH 03104",
    phone: "",
    website: "https://cac-nh.org/behavioral-health-services/",
    email: "",
    services: [
      "Trauma screening and assessment",
      "Trauma-focused cognitive behavioral therapy",
      "Child and family traumatic stress intervention",
      "Referral assistance"
    ],
    description: "Child advocacy center behavioral health program supporting children and families after disclosure of abuse or neglect.",
    notes: "Access services through the child advocacy center team. Offers on-site and telehealth care; this is not a general walk-in mental health clinic."
  },
  {
    id: 451,
    name: "Generations Geriatric Mental Health",
    category: "Primary Mental Health Providers",
    counties: [
      "Hillsborough"
    ],
    statewide: false,
    address: "138 Webster Street, Manchester, NH 03104",
    phone: "(603) 645-5977",
    website: "https://ggmh.org/index.html",
    email: "",
    services: [
      "Geriatric psychiatric care",
      "Mental health assessment",
      "Psychological and neuropsychological services"
    ],
    description: "Manchester practice specializing in mental health concerns affecting older adults.",
    notes: "Contact the practice for intake, referral requirements and insurance participation."
  },
  {
    id: 452,
    name: "Triangle Club – Dover",
    category: "Online Resources and Support Groups",
    counties: [
      "Strafford"
    ],
    statewide: false,
    address: "120 Broadway, Dover, NH 03820",
    phone: "(603) 742-9803",
    website: "https://www.triangleclubdovernh.org/",
    email: "",
    services: [
      "In-person recovery meetings",
      "Peer-driven recovery support",
      "Meeting schedule and community resources"
    ],
    description: "Dover meetinghouse offering peer-driven support for people affected by alcohol and other substance use.",
    notes: "Use the official meeting schedule to find a suitable group. Peer meetings are not medical treatment."
  },
  {
    id: 453,
    name: "White Mountains Recovery Homes",
    category: "Housing",
    subcategory: "Recovery Homes",
    counties: [
      "Grafton"
    ],
    statewide: false,
    address: "669 Union Street, Littleton, NH 03561",
    phone: "(508) 326-2913",
    website: "https://www.whitemountainsrecovery.com/",
    email: "",
    services: [
      "Recovery housing",
      "Structured recovery support",
      "Referral and telephone screening"
    ],
    description: "Recovery homes offering a structured, primarily 12-step living environment in northern New Hampshire.",
    notes: "Medication-assisted treatment is accepted. Apply by telephone screening; review house-specific intake criteria, fees and availability. Housing is not medical detoxification."
  },
  {
    id: 454,
    name: "Fellowship Housing Opportunities",
    category: "Housing",
    subcategory: "General Housing",
    counties: [
      "Merrimack"
    ],
    statewide: false,
    address: "36 Pleasant Street, Concord, NH 03301",
    phone: "(603) 225-1767",
    website: "https://www.fellowshiphousing.org/",
    email: "",
    services: [
      "Supportive housing",
      "Mental health recovery support",
      "Community outreach services"
    ],
    description: "Concord organization providing housing and practical supports for people recovering from psychiatric illness.",
    notes: "Contact the organization about housing eligibility and referral pathways. Outreach services are coordinated with the local community mental health center."
  },
  {
    id: 455,
    name: "Helping Hands Outreach Center – Recovery Housing",
    category: "Housing",
    subcategory: [
      "Shelters",
      "Recovery Homes"
    ],
    counties: [
      "Hillsborough"
    ],
    statewide: false,
    address: "50 Lowell Street and 142 Central Street, Manchester, NH",
    phone: "",
    website: "https://helpinghandsmanchesternh.com/",
    email: "",
    services: [
      "Men's recovery shelter",
      "Gendron House sober living",
      "Case management",
      "Behavioral health and peer support"
    ],
    description: "Manchester residential programs supporting men affected by homelessness and addiction.",
    notes: "Use the official application pathway to ask about admission. The recovery shelter and Gendron House provide different stages of residential support."
  },
  {
    id: 456,
    name: "Lydia's House of Hope",
    category: "Housing",
    subcategory: "General Housing",
    counties: [
      "Strafford"
    ],
    statewide: false,
    address: "21 Grand Street, Somersworth, NH 03878",
    phone: "(603) 692-3100",
    website: "https://www.lydiashousenh.org/lydias-house-of-hope/",
    email: "",
    services: [
      "Residential support for women and children",
      "Case management",
      "Counseling and recovery programming",
      "Independent-living skills"
    ],
    description: "Structured, faith-based residential program helping women and children leave homelessness.",
    notes: "Requires a minimum year-long commitment. This is a residential support program, not an emergency walk-in shelter or medical detox facility."
  },
  {
    id: 457,
    name: "Waypoint – Youth Street Outreach",
    category: "Housing",
    subcategory: "Homelessness",
    counties: [
      "Hillsborough",
      "Strafford",
      "Rockingham"
    ],
    statewide: false,
    address: "Manchester, Strafford County and eastern Rockingham County, NH",
    phone: "(603) 722-8931",
    website: "https://waypointnh.org/programs/street-outreach-programs/",
    email: "",
    services: [
      "Youth homelessness outreach",
      "Shelter and resource connections",
      "Supportive counseling and crisis intervention",
      "Transportation and case management"
    ],
    description: "Free outreach for young people ages 12–24 who have run away, are homeless or face housing instability.",
    notes: "No referral is required. Outreach coverage is Manchester, Strafford County and eastern Rockingham County; contact youth navigators for assistance."
  },
  {
    id: 458,
    name: "Second Growth – Youth Prevention and Counseling",
    category: [
      "Primary SUD Providers",
      "Therapists"
    ],
    subcategory: "Treatment Centers",
    counties: [
      "Grafton",
      "Sullivan"
    ],
    statewide: false,
    address: "205 Billings Farm Road, #1B-1, White River Junction, VT 05001",
    phone: "(802) 295-9800",
    website: "https://www.secondgrowth.org/support-groups-counseling/",
    email: "",
    services: [
      "Substance use prevention",
      "Individual and family counseling",
      "Youth support groups",
      "School-based student assistance"
    ],
    description: "Upper Valley organization providing substance use prevention and counseling, with documented youth programming in New Hampshire including Claremont schools.",
    notes: "Call to arrange counseling or group intake. NH school-based programming is documented at https://www.secondgrowth.org/our-staff/. Office-based services are in Vermont."
  },
  {
    id: 459,
    name: "Amethyst Foundation – Impaired Driver Care Management",
    category: "Primary SUD Providers",
    subcategory: "Treatment Centers",
    counties: [
      "Rockingham"
    ],
    statewide: false,
    address: "120 Hedding Road, Epping, NH 03042",
    phone: "(603) 679-2100",
    website: "https://www.amethystfoundation.com/intake_interview.html",
    email: "",
    services: [
      "Alcohol and drug screening",
      "Substance use evaluation",
      "Care-management planning",
      "Impaired-driver education"
    ],
    description: "New Hampshire impaired-driver program providing screening, evaluation and coordination of required services.",
    notes: "Contact the program before enrolling to confirm the requirements for the driver's specific NH or out-of-state case, current fees and required records."
  },
  {
    id: 460,
    name: "QuitNow New Hampshire",
    category: "Online Resources and Support Groups",
    counties: [],
    statewide: true,
    address: "",
    phone: "1-800-784-8669",
    website: "https://quitnownh.org/about-us/",
    email: "",
    services: [
      "Tobacco quit coaching",
      "Nicotine-replacement guidance",
      "Telephone cessation support"
    ],
    description: "New Hampshire-specific tobacco cessation pathway offering quit coaching and support to NH residents.",
    notes: "Call 1-800-QUIT-NOW to enroll. Coaching and eligible nicotine-replacement support are offered at no cost."
  },
  {
    id: 461,
    name: "The Center for Safer Communities (formerly Turning Points Network)",
    category: [
      "Crisis Intervention Services",
      "Housing"
    ],
    subcategory: "Shelters",
    counties: [
      "Sullivan"
    ],
    statewide: false,
    address: "Sullivan County, NH",
    phone: "1-800-639-3130",
    website: "https://cscnh.org/",
    email: "",
    services: [
      "Crisis and support line",
      "Survivor advocacy",
      "Emergency shelter",
      "Safety planning"
    ],
    description: "Sullivan County agency providing confidential crisis support to survivors of domestic violence, sexual violence and stalking.",
    notes: "Direct services are free and confidential. Contact the support line for safety planning and shelter access; shelter addresses are not published."
  },
  {
    id: 462,
    name: "True North Healing Center",
    category: "Therapists",
    counties: [
      "Hillsborough"
    ],
    statewide: true,
    address: "40 South River Road, Unit 53, Bedford, NH 03110; 149 Hanover Street, Suite 200, Manchester, NH 03101",
    phone: "(603) 696-4872",
    website: "https://truenorthhealingcenter.com/",
    email: "",
    services: [
      "Trauma-focused psychotherapy",
      "EMDR therapy",
      "Veteran and first-responder therapy",
      "Statewide telehealth"
    ],
    description: "NH counseling practice treating trauma and related mental health concerns, including care for veterans and first responders.",
    notes: "Offers in-person sessions in Bedford and Manchester and telehealth across NH. Accepts several insurance plans and VA Community Care; confirm coverage and any required authorization. Not a free equine-therapy program."
  },
];
